/** Editor screen: chrome around the Editor core (toolbar, chips, sheets, share). */
import type { Callout, CalloutRange, Elevation, Highlight, Item, MapDoc, Note, TestMethod, TestPoint, TestStyle, Vec, WorkArea } from '../../shared/doc.ts';
import { ELEV_PREFIX_DEFAULT, MATERIALS, TEST_METHODS, TEST_STYLES, cleanElevation, markupOf, materialByKey, placementOf, plainOf, tidyRuns, type TextRun } from '../../shared/doc.ts';
import { FAIL_LABEL, isSharedLabel, markupLabelId, markupOfLabel } from '../model/markups.ts';
import { depthText, elevationsText, isElevation, mapPrefix, prefixOf, shownValue } from '../model/elev.ts';
import { deleteMap, mapImage, renameMap, restyleTests, saveDoc, saveThumb } from '../app/maps.ts';
import { navigate } from '../app/router.ts';
import { app, updateSettings } from '../app/state.ts';
import { ensureImage } from '../app/sync.ts';
import { takeWarm } from '../app/warm.ts';
import { liftInWorker } from '../render/lift.ts';
import { copyPng, copyText, downloadBlob, safeFilename, shareFile } from '../app/share.ts';
import { Editor, type ToolId } from '../editor/editor.ts';
import type { PenKind } from '../editor/pen.ts';
import { analysePlan } from '../app/vision.ts';
import { calloutIsEmpty, maskStation, parseStation, stationCaret, stationCharsAfter } from '../model/station.ts';
import { TEST_METHOD_LABEL, badgeName, badgeRef, methodOf, ownNameOf, testLabelText, testName, yyyymmddToIso, shownNumber, nameOfNumber } from '../model/testid.ts';
import { loadFonts } from '../render/fonts.ts';
import { areaLabelText, calloutColor, calloutLines, calloutMaterialName, failLabelText, sharedLabelColor } from '../render/labels.ts';
import { TEXT_COLORS, textColor } from '../render/rich.ts';
import { lookRows, richField, type RichField } from './richtext.ts';
import { createSide, sideChoices, sideField, sideHelp, sideLabel, sideRow, sideRows, sideSwitch, type SideNav, type SidePage } from './side.ts';
import { AREA_COLORS, ELEV_COLORS, FAIL, INK, NOTE_COLORS, PASS, areaColors, darkOf, elevColor, elevColorName, elevInk, materialColor, noteColor, tintOf } from '../render/style.ts';
import { elevColourPicker } from './elevcolour.ts';
import { createNumpad, spotFor, type PadKey, type Rect as PadRect } from './numpad.ts';
import { kept as keptPad, padBounds, padField, padGone, padKeepOut, rememberPad } from './fieldpad.ts';
import { markIdsOf } from '../model/arrows.ts';
import { refreshGlass, setGlassSource, trackGlass } from './glass.ts';
import { BarPlace, visibleSpan, type BarInput, type Rect as BarRect } from './barspot.ts';
import { sharedMeasurer } from '../render/text.ts';
import { getMap } from '../store/idb.ts';
import { h, icon, ICONS, toast, dismissToast, clear, haptic, append, thumbGroup, fillGroup, placeThumb, reconcile, segmented, isGlass } from './dom.ts';
import { closeAll, confirmSheet, MENU_SEP, menuOpen, openMenu, openPopover, promptSheet, type MenuItem } from './sheet.ts';
import * as zoom from './zoom.ts';

/**
 * The tools, each staying in hand until another is chosen. Magic Mark, in hand as a map opens,
 * reads the plan under a stroke (hold, then draw). The Pen places a shape point by point (a tap on
 * a mark takes it in hand). Test, on a soils map, places a test with every tap. Shapes draws a
 * square (or, chosen beside the tools, a circle) by a drag. Callout calls out a mark or a spot.
 * Every tool is as wide as the widest: the toolbar never changes width, and the choice slides.
 */
const TOOLS: Record<MapDoc['kind'], { id: ToolId | 'shape'; label: string; icon: string; short?: string }[]> = {
  soils: [
    { id: 'mark', label: 'Magic Mark', icon: ICONS.magic, short: 'Magic' },
    { id: 'pen', label: 'Pen', icon: ICONS.pen },
    { id: 'test', label: 'Test', icon: ICONS.test },
    { id: 'elev', label: 'Elevation', icon: ICONS.elev, short: 'Elev.' },
    { id: 'shape', label: 'Shapes', icon: ICONS.shapes },
    { id: 'callout', label: 'Callout', icon: ICONS.callout },
  ],
  concrete: [
    { id: 'mark', label: 'Magic Mark', icon: ICONS.magic, short: 'Magic' },
    { id: 'pen', label: 'Pen', icon: ICONS.pen },
    { id: 'shape', label: 'Shapes', icon: ICONS.shapes },
    { id: 'callout', label: 'Callout', icon: ICONS.callout },
  ],
};
/** Square and Circle share one place in the toolbar, Shapes: it takes a square; the strip beside the tools offers the circle. */
const SHAPES: { id: 'square' | 'circle'; label: string; icon: string }[] = [
  { id: 'square', label: 'Square', icon: ICONS.toolSquare },
  { id: 'circle', label: 'Circle', icon: ICONS.toolCircle },
];
const isShape = (t: ToolId): t is 'square' | 'circle' => t === 'square' || t === 'circle';
const DEFAULT_ALIGNMENTS = ['SC1', 'SC2', 'SC1/SC2', 'PDM/TEL', 'Mainline'];
/**
 * What a markup's label usually says, as inspectors write it (docs/research/elevations.md): what was
 * observed, never "approved" — that is the geotechnical engineer's word.
 */
const MARKUP_PHRASES = [
  'Over-excavation performed today',
  'Previous over-excavation',
  'Removal bottom observed',
  'Bottom scarified and recompacted',
  'Fill placed and compacted today',
  'Previous fill',
  'Keyway observed',
  'Subgrade observed',
  'Trench backfill observed',
  'Footing excavations observed',
  'Work area observed',
];
const FAIL_PHRASES = ['Failing area', 'Additional removal required', 'Unsuitable material, to be removed', 'Area to be reworked and retested'];
/** What a tag may call an elevation: "Elev." (the plainest: "EL" is also an easement line), the grades shot most, or nothing. */
const ELEV_WORDS: [string, string][] = [
  ['Elev.', 'elevation'],
  ['BOR', 'bottom of removal'],
  ['BOE', 'bottom of excavation'],
  ['FSG', 'finish subgrade'],
  ['SG', 'subgrade'],
  ['FG', 'finish grade'],
  ['EG', 'existing grade'],
  ['BOF', 'bottom of footing'],
  ['', 'No word, the elevation alone'],
];
/** The grades a shot is measured against. */
const ELEV_GRADES: [string, string][] = [
  ['FG', 'finish grade'],
  ['FSG', 'finish subgrade'],
  ['PAD', 'pad grade'],
  ['EG', 'existing grade'],
  ['BOF', 'bottom of footing'],
];
const ELEV_WHAT = ['bottom of over-excavation', 'removal bottom', 'keyway bottom', 'subgrade', 'finish grade', 'trench bottom', 'test elevation'];
const ELEV_SOURCES = ['GPS rover, contractor', 'laser and rod', "surveyor's stakes", 'estimated from plan'];
const MARK_HINT = 'Hold a moment, then draw along the plan';
const PEN_HINT = 'Tap each corner · hold to place it exactly';
const SQUARE_HINT = 'Drag to draw a square';
const CIRCLE_HINT = 'Drag out a circle from its middle';
const TEST_HINT = 'Tap where each test was taken';
const ELEV_HINT = 'Tap where each elevation was shot, then type it';
const CALLOUT_HINT = { soils: 'Tap a work area to call it out', concrete: 'Tap a mark to call it out' };
/** Every hint the editor gives: none outlives the map it was about. */
const HINTS = [MARK_HINT, PEN_HINT, SQUARE_HINT, CIRCLE_HINT, TEST_HINT, ELEV_HINT, CALLOUT_HINT.soils, CALLOUT_HINT.concrete];
/** What the next tap does with a tool in hand. */
function hintFor(tool: ToolId, kind: MapDoc['kind']): string | null {
  if (tool === 'callout') return CALLOUT_HINT[kind];
  return tool === 'mark' ? MARK_HINT : tool === 'pen' ? PEN_HINT : tool === 'square' ? SQUARE_HINT : tool === 'circle' ? CIRCLE_HINT : tool === 'test' ? TEST_HINT : tool === 'elev' ? ELEV_HINT : null;
}
/** Room for the caret after a callout field's text, px (the same as `--caret-room` in app.css). */
const CARET_ROOM = 4;
/**
 * `--spring` and `--spring-soft` in app.css: one that lands without a wobble (the strip's glass
 * taking its new width), one with a breath of overshoot. Cubic curves, not `linear()` springs:
 * Safari runs animations on those on the main thread, where a tap's work would make them stutter.
 */
const SPRING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SPRING_SOFT = 'cubic-bezier(0.3, 1.3, 0.5, 1)';
const reducedMotion = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches;

export function renderEditor(root: HTMLElement, id: string): () => void {
  const stage = h('div', { class: 'stage loading' });
  // No bar across the plan. Top left: the way out, and the map's name (tap to rename). Top right:
  // undo, redo and more in one capsule, and Share. Bottom middle: the tools and what the next mark
  // will be. Bottom right: fit (and zoom steps, for a mouse).
  const backBtn = h('button', { class: 'glass round', type: 'button', 'aria-label': 'Back to library', onClick: () => leave() }, icon(ICONS.back, 24));
  // (its words a span of their own: the button's glass is never wiped with them — ui/glass.ts)
  const titleText = h('span', { class: 'title-text' });
  const title = h('button', { class: 'glass title', type: 'button', title: 'Rename' }, titleText);
  const undoBtn = h('button', { class: 'btn icon', type: 'button', 'aria-label': 'Undo', disabled: true }, icon(ICONS.undo));
  const redoBtn = h('button', { class: 'btn icon', type: 'button', 'aria-label': 'Redo', disabled: true }, icon(ICONS.redo));
  const moreBtn = h('button', { class: 'btn icon', type: 'button', 'aria-label': 'More' }, icon(ICONS.more));
  const shareBtn = h('button', { class: 'tinted share', type: 'button', 'aria-label': 'Share' }, icon(ICONS.share, 21), h('span', { class: 'label' }, 'Share'));
  const topChrome = h('div', { class: 'chrome top' }, h('div', { class: 'cluster lead' }, backBtn, title), h('div', { class: 'cluster trail' }, h('div', { class: 'glass group' }, undoBtn, redoBtn, moreBtn), shareBtn));
  const toolbar = thumbGroup(h('div', { class: 'glass toolbar', role: 'toolbar' }));
  // the strip beside (or above) the tools: a concrete map's pours and materials, a soils map's date · initials · number
  const strip = (cls: string) => {
    const scroll = h('div', { class: 'scroll' });
    const el = h('div', { class: `glass strip ${cls}`, hidden: true }, scroll);
    let morphing = 0;
    let widthAnim: Animation | null = null;
    let settle: (() => void) | null = null;
    // its contents fade out at an end only where there is more to scroll to (not while the glass is still taking its width)
    const fades = () => {
      if (morphing) return;
      el.classList.toggle('fade-l', scroll.scrollLeft > 2);
      el.classList.toggle('fade-r', scroll.scrollLeft < scroll.scrollWidth - scroll.clientWidth - 2);
    };
    scroll.addEventListener('scroll', fades, { passive: true });
    new ResizeObserver(fades).observe(scroll);
    /**
     * What it holds, made again: the glass goes from the width it had to the width it needs on the
     * same spring as everything else, never a jump; for another tool (`mode`), what it held fades as
     * what it holds now comes in. Called again mid-way, it carries on from where the glass is.
     */
    const morph = (mode: string, render: () => void) => {
      // (the first time, it simply comes in with the rest of the controls)
      const first = el.dataset.mode === undefined;
      const changed = !first && el.dataset.mode !== mode;
      el.dataset.mode = mode;
      const shown = !first && !el.hidden && el.isConnected && el.offsetParent !== null && !reducedMotion();
      // (offsetWidth, not a rect: an entrance's scale is no part of the width)
      const w0 = shown ? el.offsetWidth : 0;
      // (its width now, part-way through the last change, is where this one starts: so the cancel first)
      widthAnim?.cancel();
      widthAnim = null;
      let ghost: HTMLElement | null = null;
      if (shown && changed) {
        ghost = scroll.cloneNode(true) as HTMLElement;
        ghost.classList.add('ghost');
        ghost.inert = true;
        ghost.setAttribute('aria-hidden', 'true');
        ghost.style.translate = `${-scroll.scrollLeft}px 0`;
      }
      render();
      if (!shown) return fades();
      const w1 = el.offsetWidth;
      const done = () => {
        morphing = Math.max(0, morphing - 1);
        if (morphing) return;
        fades();
        const s = settle;
        settle = null;
        s?.();
      };
      if (Math.abs(w1 - w0) > 0.5) {
        morphing++;
        el.classList.remove('fade-l', 'fade-r');
        const a = (widthAnim = el.animate([{ width: `${w0}px` }, { width: `${w1}px` }], { duration: 480, easing: SPRING }));
        a.onfinish = a.oncancel = done;
      } else fades();
      if (ghost) {
        el.append(ghost);
        const g = ghost;
        g.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 130, easing: 'ease-out', fill: 'forwards' }).onfinish = () => g.remove();
        setTimeout(() => g.remove(), 400);
        scroll.animate([{ opacity: 0, transform: 'translateX(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 50, easing: SPRING_SOFT, fill: 'backwards' });
      }
    };
    /** What needs the strip at its final width (keeping the chosen chip in view): once the glass has it. */
    const settled = (fn: () => void) => {
      if (morphing) settle = fn;
      else requestAnimationFrame(fn);
    };
    return { el, scroll, fades, morph, settled };
  };
  const subtools = strip('subtools');
  const infobar = strip('infobar');
  // what the next work area is (and the selected one's): a markup's, or a failing area
  const areaKinds = thumbGroup(h('div', { class: 'set markups', role: 'group', 'aria-label': 'Markup' }));
  // how the next test is taken: by nuclear gauge or by sand cone, each numbered on its own (a placed test is switched on its own bar)
  const testMethods = thumbGroup(h('div', { class: 'set materials', role: 'group', 'aria-label': 'Test method' }));
  const pours = thumbGroup(h('div', { class: 'set placements', role: 'group', 'aria-label': 'Pour' }));
  const materials = thumbGroup(h('div', { class: 'set materials', role: 'group', 'aria-label': 'Material' }));
  // which shape Shapes draws: offered beside the tools while it is in hand
  const shapeKinds = thumbGroup(h('div', { class: 'set shapes', role: 'group', 'aria-label': 'Shape' }));
  const dock = h('div', { class: 'chrome bottom', hidden: true }, toolbar, subtools.el, infobar.el);
  // the tools stand still, whatever the strip beside them holds: it is placed from their size (app.css)
  new ResizeObserver(() => {
    if (!toolbar.offsetWidth) return;
    dock.style.setProperty('--tw', `${toolbar.offsetWidth}px`);
    dock.style.setProperty('--th', `${toolbar.offsetHeight}px`);
  }).observe(toolbar);
  const context = h('div', { class: 'glass context' });
  // a callout is edited in place: this box sits exactly over it, styled as the callout is drawn
  const inplace = h('div', { class: 'callout-inplace', hidden: true });
  const zoomer = h('div', { class: 'glass zoomer', hidden: true });
  // (installed, the status bar is white type over the plan: a breath of shade under it keeps it read)
  const scrim = h('div', { class: 'scrim' });
  const screen = h('div', { class: 'screen editor' }, stage, scrim, topChrome, dock, zoomer, inplace, context);
  root.append(screen);
  // what can be changed about the selection, beside the plan: the inspector (ui/side.ts) at the right,
  // between the controls at the top and those at the foot (the tools, the strip, Fit) — a bubble there
  // when it is closed, which opens it on everything about the selection (its listeners go with the screen)
  const sideAc = new AbortController();
  const side = createSide(screen, () => {
    const sr = screen.getBoundingClientRect();
    const top = topChrome.getBoundingClientRect().bottom - sr.top + 10;
    const lows = [dock, zoomer].filter((el) => !el.hidden && el.offsetParent !== null).map((el) => el.getBoundingClientRect().top - sr.top - 10);
    return { top, bottom: Math.min(sr.height - 12, ...lows) };
  }, { signal: sideAc.signal, onBubble: () => openInspector(editor?.selection ?? null, 'all') });
  // every glass control of the editor, kept up with the plan under it (ui/glass.ts)
  const untrackGlass = [backBtn, title, ...topChrome.querySelectorAll<HTMLElement>('.glass'), shareBtn, toolbar, subtools.el, infobar.el, zoomer, context, side.el, side.bubble].map((el) => trackGlass(el));

  let editor: Editor | null = null;
  let destroyed = false;
  let saveTimer = 0;
  /** Each method's highest number on the other maps of the day on this device: numbering goes on from there. */
  const numberFloor: Record<TestMethod, number> = { nuke: 0, sandcone: 0 };
  let mapTitle = '';
  let imageBitmap: ImageBitmap | null = null;
  const ac = new AbortController();

  // The document is saved half a second after every change. Its thumbnail — a full render of the
  // plan, which only the library and other devices ever see — is made when the map is left or the
  // app put away, and at most once a minute while work goes on. A map opened and closed unchanged
  // saves (and uploads) nothing.
  let lastSaved: MapDoc | null = null; // the document as last written to the store
  let thumbOf: MapDoc | null = null; // the document the stored thumbnail shows
  let thumbAt = Date.now();
  const persist = async (doc: MapDoc, leaving: boolean) => {
    if (!editor) return;
    const docNew = doc !== lastSaved;
    const thumbDue = doc !== thumbOf && (leaving || Date.now() - thumbAt > 60_000);
    if (!docNew && !thumbDue) return;
    lastSaved = doc;
    let thumb: Blob | undefined;
    if (thumbDue) {
      try {
        thumb = await editor.thumbnail();
        thumbOf = doc;
        thumbAt = Date.now();
      } catch {
        /* thumbnail is best-effort */
      }
    }
    // (a shot whose elevation is still being typed, nothing yet, is no shot: it is never kept)
    if (docNew) await saveDoc(id, doc.items.some(untyped) ? { ...doc, items: doc.items.filter((i) => !untyped(i)) } : doc, thumb);
    else if (thumb) await saveThumb(id, thumb);
  };
  const untyped = (i: Item): boolean => i.t === 'elev' && !isElevation(i.value);
  const scheduleSave = (doc: MapDoc) => {
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => void persist(doc, false), 500);
  };
  const flushSave = async () => {
    clearTimeout(saveTimer);
    if (editor) await persist(editor.doc, true);
  };

  /**
   * Back to the library: the plan shrinks into its card (zoom.ts). What flies is made here, while
   * the editor still stands — a copy of its canvases as they are on screen, the whole map drawn
   * small to land as the card's picture, and the controls, to fade.
   */
  function leave(): void {
    const ed = editor;
    if (ed && !document.querySelector('dialog[open]')) {
      endCalloutEdit();
      ed.endElevEdit();
      ed.flush();
      const shot = zoom.copyStage(stage);
      const v = ed.view;
      const sr = stage.getBoundingClientRect();
      const clones = [scrim, topChrome, dock, zoomer, context].filter((el) => !el.hidden && (el !== context || el.classList.contains('show'))).map((el) => el.cloneNode(true) as HTMLElement);
      zoom.prepareBack({ id, shot, plan: ed.snapshot(640), planRect: { x: sr.left + v.tx, y: sr.top + v.ty, w: ed.doc.w * v.scale, h: ed.doc.h * v.scale }, chrome: clones });
    }
    navigate({ name: 'library' });
  }

  /* ------------------------------------------------------------ chrome */

  /** The tools are made once; choosing one slides the thumb under it. */
  function renderToolbar(): void {
    if (!editor) return;
    const ed = editor;
    const pressed = (id: string) => (id === 'shape' ? isShape(ed.tool) : id === ed.tool);
    if (!toolbar.querySelector(':scope > .tool')) {
      dock.dataset.kind = ed.doc.kind;
      fillGroup(
        toolbar,
        // (a phone's narrow row names a tool shortly: "Magic", "Elev.")
        TOOLS[ed.doc.kind].map((t) => {
          const b: HTMLButtonElement = h('button', { class: 'tool', type: 'button', 'aria-label': t.label, dataset: { tool: t.id }, 'aria-pressed': String(pressed(t.id)) }, icon(t.icon, 25), h('span', { class: 'long', 'aria-hidden': 'true' }, t.label), h('span', { class: 'short', 'aria-hidden': 'true' }, t.short ?? t.label));
          // (Shapes: a square, unless a shape is already in hand — the circle is chosen beside the tools)
          b.addEventListener('click', () => {
            const was = ed.tool;
            ed.toggleTool(t.id === 'shape' ? (isShape(ed.tool) ? ed.tool : 'square') : (t.id as ToolId));
            haptic();
            // the tool already in hand, chosen again: it says again what it does (a new tool says so as it is taken)
            if (ed.tool === was) {
              const hint = hintFor(ed.tool, ed.doc.kind);
              if (hint) toast(hint, 'info', 7000);
            }
          });
          return b;
        }),
      );
      return;
    }
    for (const b of toolbar.querySelectorAll<HTMLElement>('.tool')) b.setAttribute('aria-pressed', String(pressed(b.dataset.tool ?? '')));
    placeThumb(toolbar);
  }
  /** The shape Shapes draws, beside the tools while it is in hand (square or circle). */
  function fillShapes(ed: Editor): HTMLElement {
    fillGroup(shapeKinds, SHAPES.map((s) => h('button', { class: 'chip swatch-only shape', type: 'button', 'aria-label': s.label, title: s.label, 'aria-pressed': String(ed.tool === s.id), onClick: () => (ed.setTool(s.id), haptic()) }, icon(s.icon, 22))));
    return shapeKinds;
  }
  function renderSubtools(): void {
    if (!editor) return;
    const ed = editor;
    subtools.el.hidden = ed.doc.kind !== 'concrete';
    if (ed.doc.kind !== 'concrete' || renderTask(subtools)) return;
    const mode = isShape(ed.tool) ? 'shape' : 'draw';
    subtools.morph(mode, () => {
      // (each group is in the strip before it is filled: its thumb is placed under the chip that is
      // pressed; and the groups already there stay put, so a thumb sliding goes on sliding)
      const moreBtn: HTMLButtonElement = h('button', { class: 'chip swatch-only more-colours', type: 'button', 'aria-label': 'Colours', title: 'Colours', onClick: () => openMaterialColours(moreBtn) }, h('span', { class: 'swatch' }));
      reconcile(subtools.scroll, [mode === 'shape' && shapeKinds, mode === 'shape' && h('div', { class: 'divider' }), h('span', { class: 'caption' }, 'Pour'), pours, h('div', { class: 'divider' }), materials, moreBtn]);
      if (mode === 'shape') fillShapes(ed);
      // Pours (placements): the chips say which pour the NEXT mark goes into, and only that —
      // they never move a mark (a selected mark's pour is changed from its own panel).
      // + starts the next pour; it is off while the new one is already being marked.
      const cur = ed.placement;
      fillGroup(pours, [
        ed.placements.map((n) => h('button', { class: 'chip num', type: 'button', 'aria-label': `Pour ${n}`, 'aria-pressed': String(cur === n), onClick: () => (ed.setPlacement(n), haptic(), renderSubtools()) }, String(n))),
        h('button', { class: 'chip num new', type: 'button', 'aria-label': 'New pour', disabled: cur > ed.placementCount, onClick: () => (ed.newPlacement(), haptic(), renderSubtools()) }, icon(ICONS.plus, 20)),
      ]);
      // the material chips show the selected mark's material (or the one the next mark gets) and change
      // it: the named ones, then one colour — the one last chosen from the colours (Concrete green at
      // first), or the selected mark's own — a tap away; the rest of the colours are a tap more
      const current = ed.selectionMaterial ?? ed.material;
      const now = materialByKey(current);
      const other = now.swatchOnly && now.key === current ? now : materialByKey(app.state.settings.otherMaterial);
      fillGroup(materials, [
        ...MATERIALS.filter((m) => !m.swatchOnly).map((m) => h('button', { class: 'chip', type: 'button', 'aria-label': m.name, 'aria-pressed': String(current === m.key), onClick: () => pickMaterial(m.key) }, h('span', { class: 'swatch', style: { background: m.color } }), m.name)),
        h('button', { class: 'chip swatch-only', type: 'button', 'aria-label': other.name, title: other.name, 'aria-pressed': String(current === other.key), onClick: () => pickMaterial(other.key) }, h('span', { class: 'swatch', style: { background: other.color } })),
      ]);
    });
    // the chosen pour stays in view when the row is wider than the screen (once the glass has its width)
    subtools.settled(() => {
      const sc = subtools.scroll;
      const pressed = pours.querySelector<HTMLElement>('.chip[aria-pressed="true"]');
      if (pressed) {
        const left = pours.offsetLeft + pressed.offsetLeft - sc.scrollLeft;
        if (left < 12 || left + pressed.offsetWidth > sc.clientWidth - 12) sc.scrollLeft = Math.max(0, pours.offsetLeft + pressed.offsetLeft - 48);
      }
      subtools.fades();
    });
  }
  /** A concrete material chosen: the next mark's (and the selected mark's, with its callouts); a colour is remembered as the one offered at a tap. */
  function pickMaterial(key: string): void {
    const ed = editor;
    if (!ed) return;
    ed.applyMaterial(key);
    if (materialByKey(key).swatchOnly && materialByKey(key).key === key && app.state.settings.otherMaterial !== key) void updateSettings({ otherMaterial: key });
    haptic();
    renderSubtools();
    nameColourOnly();
  }
  /** The colours a concrete mark may take besides the named materials, in a glass popover: each its own word in the legend. */
  function openMaterialColours(anchor: HTMLElement): void {
    const ed = editor;
    if (!ed) return;
    const current = ed.selectionMaterial ?? ed.material;
    const colours = MATERIALS.filter((m) => m.swatchOnly);
    const named = (m: (typeof MATERIALS)[number]) => (m.long.endsWith('(as noted)') ? m.name : `${m.name} · ${m.long}`);
    const caption = h('div', { class: 'palette-caption' }, named(colours.find((m) => m.key === current) ?? materialByKey(app.state.settings.otherMaterial)));
    openPopover(anchor, (close) => [
      h('div', { class: 'palette', role: 'group', 'aria-label': 'Colours' },
        ...colours.map((m) => h('button', { class: 'sw', type: 'button', 'aria-label': m.name, title: m.name, 'aria-pressed': String(current === m.key), style: { '--sw-fill': m.color, '--sw-stroke': darkOf(m.color) }, onClick: () => (pickMaterial(m.key), close()) })),
      ),
      caption,
    ], { above: true, label: 'Colours' });
  }
  /**
   * Soils only, where a concrete map has its pours and materials: what the next tap or stroke makes,
   * for the tool in hand. Drawing an area: the markups (each its own colour and label), Failing, and
   * the markup's colour. Test: how the next test is taken, and its number. Elevation: the next
   * shot's number, and what its tag calls an elevation.
   */
  function renderInfobar(): void {
    if (!editor) return;
    const ed = editor;
    const d = ed.doc;
    infobar.el.hidden = d.kind !== 'soils';
    if (d.kind !== 'soils' || renderTask(infobar)) return;
    const mode = ed.tool === 'test' ? 'test' : ed.tool === 'elev' ? 'elev' : isShape(ed.tool) ? 'shape' : 'area';
    infobar.morph(mode, () => {
      if (mode === 'test') {
        const method = ed.testMethod;
        const next = ed.nextNumber(method, numberFloor[method]);
        reconcile(infobar.scroll, [
          testMethods,
          h('div', { class: 'divider' }),
          // the next test's number; tapped, how the tests are labelled (and, for the date-and-initials style, those)
          h('button', { class: 'part', type: 'button', 'aria-label': `Next test number ${next}: test labels`, onClick: () => void editLabels() }, icon(ICONS.tag, 20), h('span', { class: 'k' }, 'Next'), h('span', { class: 'v' }, `#${next}`)),
        ]);
        fillGroup(testMethods, TEST_METHODS.map((v) => h('button', { class: 'chip', type: 'button', 'aria-label': TEST_METHOD_LABEL[v], 'aria-pressed': String(method === v), onClick: () => (ed.setNextTestMethod(v), haptic(), renderInfobar()) }, TEST_METHOD_LABEL[v])));
      } else if (mode === 'elev') {
        const word = mapPrefix(d.elev);
        const wordBtn: HTMLButtonElement = h('button', { class: 'part', type: 'button', 'aria-label': `Tags say ${word || 'no word'} before the elevation`, onClick: () => chooseElevWord(wordBtn, null) }, h('span', { class: 'k' }, 'Tag'), h('span', { class: 'v' }, word || 'No word'), icon(ICONS.chevronDown, 16));
        // the tags' colour on this map: a tap opens the palette (and a colour of one's own)
        const tag = elevColor(d.elev?.color);
        const colourBtn: HTMLButtonElement = h('button', { class: 'chip colour', type: 'button', 'aria-label': `Tag colour: ${elevColorName(d.elev?.color)}`, title: 'Tag colour', onClick: () =>
          openPopover(colourBtn, (close) => [
            elevColourPicker(d.elev?.color, (c) => {
              ed.setElevMeta({ color: c === ELEV_COLORS[0]!.key ? undefined : c });
              renderInfobar();
              close();
            }),
          ], { above: true, label: 'Tag colour' }) }, h('span', { class: 'swatch', style: { background: tag } }), icon(ICONS.chevronDown, 14));
        reconcile(infobar.scroll, [
          h('button', { class: 'part', type: 'button', 'aria-label': `Next elevation number ${ed.nextElevNumber()}: elevation settings`, onClick: () => void editElevSettings() }, icon(ICONS.elev, 20), h('span', { class: 'k' }, 'Next'), h('span', { class: 'v' }, `#${ed.nextElevNumber()}`)),
          h('div', { class: 'divider' }),
          wordBtn,
          h('div', { class: 'divider' }),
          colourBtn,
        ]);
      } else {
        // the markups: each chip its number on its own colour; + begins the next; Failing makes the next area a failing one
        const failing = ed.areaFailing;
        const sel = ed.selectionMarkup;
        const cur = sel ?? ed.markup;
        const chips = ed.markups.map((n) => {
          const c = areaColors(ed.markupColour(n));
          return h('button', { class: 'chip num markup', type: 'button', 'aria-label': `Markup ${n}`, 'aria-pressed': String(!failing && cur === n), style: { '--mk-fill': c.fill, '--mk-stroke': c.stroke }, onClick: () => (ed.setMarkup(n), haptic(), renderInfobar()) }, h('span', { class: 'mk' }, String(n)));
        });
        const own = areaColors(ed.areaColor);
        const colourBtn: HTMLButtonElement = h('button', { class: 'chip colour', type: 'button', 'aria-label': `Colour of markup ${cur}`, title: 'Colour', disabled: failing, onClick: () => openPalette(colourBtn) }, h('span', { class: 'swatch', style: { background: own.fill, boxShadow: `inset 0 0 0 2px ${own.stroke}` } }), icon(ICONS.chevronDown, 14));
        // (the group is in the strip before it is filled: its thumb is placed under the chip that is pressed)
        reconcile(infobar.scroll, [mode === 'shape' && shapeKinds, mode === 'shape' && h('div', { class: 'divider' }), h('span', { class: 'caption' }, 'Markup'), areaKinds, h('div', { class: 'divider' }), colourBtn]);
        if (mode === 'shape') fillShapes(ed);
        fillGroup(areaKinds, [
          ...chips,
          h('button', { class: 'chip num new', type: 'button', 'aria-label': 'New markup', disabled: !failing && sel === null && ed.markup > ed.markupCount, onClick: () => (ed.newMarkup(), haptic(), renderInfobar()) }, icon(ICONS.plus, 20)),
          h('button', { class: 'chip fail', type: 'button', 'aria-label': 'Failing area', 'aria-pressed': String(failing), onClick: () => (ed.applyAreaFailing(!failing), haptic(), renderInfobar()) }, h('span', { class: 'swatch', style: { background: '#E0453D', boxShadow: 'inset 0 0 0 2px #B0201E' } }), 'Failing'),
        ]);
      }
    });
    // the chosen markup stays in view when the row is wider than the screen (once the glass has its width)
    infobar.settled(() => {
      const sc = infobar.scroll;
      const pressed = infobar.scroll.querySelector<HTMLElement>('.set .chip[aria-pressed="true"]');
      if (pressed) {
        const r = pressed.getBoundingClientRect(),
          sr = sc.getBoundingClientRect();
        if (r.left < sr.left + 12 || r.right > sr.right - 12) sc.scrollLeft += r.left - sr.left - 48;
      }
      infobar.fades();
    });
  }
  /**
   * The colours a markup may take, in a glass popover from the colour button: the palette (green,
   * yellow, blue …) and a well for a colour of the inspector's own. The markup in hand takes the one
   * tapped — every area of it, its label and arrows.
   */
  function openPalette(anchor: HTMLElement): void {
    const ed = editor;
    if (!ed) return;
    const colour = ed.areaColor;
    const custom = !AREA_COLORS.some((c) => c.key === colour);
    const pick = (key: string) => {
      ed.applyAreaColor(key);
      haptic();
      renderInfobar();
    };
    const well = h('input', { type: 'color', value: custom ? colour : areaColors(colour).fill, 'aria-label': 'A colour of your own' });
    well.addEventListener('change', () => pick(well.value));
    openPopover(anchor, (close) => [
      h('div', { class: 'palette', role: 'group', 'aria-label': 'Markup colour' },
        ...AREA_COLORS.map((c) => h('button', { class: 'sw', type: 'button', 'aria-label': c.name, title: c.name, 'aria-pressed': String(colour === c.key), style: { '--sw-fill': c.fill, '--sw-stroke': c.stroke }, onClick: () => (pick(c.key), close()) })),
        h('label', { class: 'sw well', title: 'A colour of your own', 'aria-pressed': String(custom), style: custom ? { '--sw-fill': colour, '--sw-stroke': areaColors(colour).stroke } : {} }, well),
      ),
    ]);
  }
  function renderZoomer(): void {
    clear(zoomer);
    zoomer.append(
      h('button', { class: 'btn icon step', type: 'button', 'aria-label': 'Zoom in', onClick: () => editor?.zoomBy(1.35) }, icon(ICONS.zoomIn)),
      h('button', { class: 'btn icon step', type: 'button', 'aria-label': 'Zoom out', onClick: () => editor?.zoomBy(1 / 1.35) }, icon(ICONS.zoomOut)),
      h('button', { class: 'btn icon', type: 'button', 'aria-label': 'Fit to screen', onClick: () => editor?.fit() }, icon(ICONS.fit)),
    );
  }
  function updateHistoryButtons(): void {
    undoBtn.disabled = !editor?.canUndo;
    redoBtn.disabled = !editor?.canRedo;
  }

  /* ------------------------------------------------ context panel (floats by the item) */

  let contextKey = ''; // what the panel currently holds, e.g. "callout:abc"
  // measured when the bar's contents or the window change — never while the plan moves under it
  let barSize: { w: number; h: number } | null = null;
  /** What the bar was last fitted beside the inspector for (its contents, the inspector's edge, the screen). */
  let barFitFor = '';
  /** Where the bar may stand, as last measured (`barRoom`). */
  let barRoomAt: ReturnType<typeof barRoom> | null = null;
  /** Where the bar stands (ui/barspot.ts): fixed to what it acts on, gliding only round to its other side or out of the inspector's way. */
  const bar = new BarPlace();
  /** Whether the bar has been placed since it came up (only coming up does it keep off a label). */
  let barShown = false;
  let barRaf = 0;
  // the stage's size, kept as it changes: the bar follows the plan on every frame of a pan without asking for a layout
  let stageW = 0,
    stageH = 0;
  new ResizeObserver(() => {
    stageW = stage.clientWidth;
    stageH = stage.clientHeight;
    barSize = null;
    barRoomAt = null;
    positionContext();
  }).observe(stage);
  // (and the controls it keeps off, as they come, go and change size)
  const roomRO = new ResizeObserver(() => {
    barRoomAt = null;
    positionContext();
  });
  for (const el of [topChrome, title, dock, toolbar, subtools.el, infobar.el, zoomer, side.el, side.bubble]) roomRO.observe(el);
  let calloutPanel: CalloutPanel | null = null;
  /**
   * A panel button. `on`: the one thing to do next (a Done), filled. `pressed`: a switch, filled
   * quietly while on — or, `fail`, in the failing red. A button that says a value ("Retest of 3",
   * "9.89 ft below FG") is plain: what it says is its state.
   */
  const chip = (label: string, onClick: () => void, opts: { icon?: string; danger?: boolean; on?: boolean; pressed?: boolean; fail?: boolean; aria?: string } = {}) =>
    h('button', { class: 'btn' + (opts.danger ? ' danger' : '') + (opts.on ? ' on' : '') + (opts.fail ? ' fail' : ''), type: 'button', 'aria-label': opts.aria, 'aria-pressed': opts.pressed === undefined ? undefined : String(opts.pressed), onClick }, opts.icon ? icon(opts.icon, 18) : null, h('span', { class: 'label' }, label));

  function renderContext(): void {
    if (!editor) return;
    syncInspector();
    syncElevField();
    // a task under way — corners being placed, a callout's arrows chosen, shapes cut out of a mark —
    // has its controls beside the tools, in the strip, never over the plan being worked on
    syncTaskStrip();
    if (editor.building !== null) {
      // corners being placed: no bar by anything (the tool's hint has had its moment)
      for (const hint of HINTS) dismissToast(hint);
      hideContext();
      return;
    }
    const sel = editor.selection;
    const it = sel && !isSharedLabel(sel) ? editor.getItem(sel) : undefined;
    const key = sel && isSharedLabel(sel) ? `label:${sel}` : it ? `${it.t}:${it.id}${it.t === 'elev' && editor.elevEditing === it.id ? ':typing' : ''}` : '';
    // typing ends when the selection moves on
    if (editingId && (it?.t !== 'callout' || it.id !== editingId)) endCalloutEdit();
    if (!key) {
      hideContext();
      return;
    }
    if (it?.t === 'callout') {
      if (editingId === it.id) calloutPanel?.sync(it);
      // the bar is rebuilt only when what it offers changes: its buttons stay under the finger
      const bk = barKeyOf(it);
      if (key !== contextKey || bk !== barKey) {
        clear(context);
        fillCalloutBar(it);
        barKey = bk;
      }
    } else {
      barKey = '';
      clear(context);
      fillContextChips(sel!, it);
    }
    // moving on to another item, the bar does not jump there: it grows out of the new one
    const moved = key !== contextKey && context.classList.contains('show');
    if (key !== contextKey || !context.classList.contains('show')) freshBar();
    contextKey = key;
    barSize = null;
    const holds = [...context.children].some((k) => !isGlass(k));
    context.classList.toggle('show', holds);
    positionContext();
    if (moved && holds) context.animate([{ opacity: 0, scale: 0.88 }, { opacity: 1, scale: 1 }], { duration: 380, easing: SPRING_SOFT });
  }
  function hideContext(): void {
    contextKey = '';
    barKey = '';
    context.classList.remove('show');
    freshBar();
  }

  /* A callout has two states, as a text box does in Freeform or Keynote. Selected: it is the
   * drawn callout — it drags, and a short bar offers Edit, Delete and More. Typed into (a fresh
   * callout, a second tap, Edit): the in-place editor covers it, and the plan lays the box out
   * from the editor's own lines, so nothing overflows and nothing moves but what is typed. */
  let editingId: string | null = null;
  const seenCallouts = new Set<string>();
  function beginCalloutEdit(id: string, at: Vec | null): void {
    const ed = editor;
    const it = ed?.getItem(id);
    if (!ed || !it || it.t !== 'callout') return;
    if (ed.selection !== id) ed.select(id);
    if (editingId !== id || !calloutPanel) {
      endCalloutEdit();
      editingId = id;
      calloutPanel = buildCalloutPanel(it);
      clear(inplace);
      inplace.append(calloutPanel.el);
      inplace.hidden = false;
      // large enough to read while typing (and Safari on iPad zooms the page for fields under 16 px)
      const r = ed.anchorRect(id);
      if (r) ed.ensureReadable(ed.metrics.fontCallout, 16, { x: r.x + r.w / 2, y: r.y + r.h / 2 });
    }
    renderContext();
    if (at) calloutPanel.focusAt(at);
    else calloutPanel.focusFirst();
  }
  function endCalloutEdit(): void {
    const id = editingId;
    if (!id) return;
    editingId = null;
    // (its alignments, if they were open: done with too)
    if (side.key() === `align:${id}`) side.close();
    calloutPanel = null;
    // (the caret out of its fields first: the keyboard and the number pad go with them)
    if (inplace.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    inplace.hidden = true;
    clear(inplace);
    editor?.setCalloutDraft(null);
    // the plan reads the stations in order: keep them that way, without a point left hanging
    const it = editor?.getItem(id);
    if (it?.t === 'callout') {
      let from = it.from.replace(/\.$/, ''),
        to = it.to.replace(/\.$/, '');
      const a = parseStation(from),
        b = parseStation(to);
      if (a !== null && b !== null && a > b) [from, to] = [to, from];
      // further stretches too — and one nobody typed a station into is gone
      const ranges = (it.ranges ?? []).flatMap((r): CalloutRange[] => {
        let f = r.from.replace(/\.$/, ''),
          t = r.to.replace(/\.$/, '');
        if (!f.trim() && !t.trim()) return [];
        const ra = parseStation(f),
          rb = parseStation(t);
        if (ra !== null && rb !== null && ra > rb) [f, t] = [t, f];
        return [{ alignment: r.alignment.trim(), from: f, to: t }];
      });
      const rangesChanged = JSON.stringify(ranges) !== JSON.stringify(it.ranges ?? []);
      if (from !== it.from || to !== it.to || rangesChanged) editor!.editCallout(id, { from, to, ...(rangesChanged ? { ranges } : {}) }, 'order');
    }
  }
  // focus moving between the callout's own fields keeps it open; anywhere else, typing is over
  inplace.addEventListener('focusout', () => {
    setTimeout(() => {
      if (!editingId || inplace.contains(document.activeElement)) return;
      endCalloutEdit();
      renderContext();
    }, 0);
  }, { signal: ac.signal });
  inplace.addEventListener('focusin', () => renderContext(), { signal: ac.signal });
  // a touch on the words between the fields goes to the nearest field
  inplace.addEventListener('mousedown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    e.preventDefault();
    const sr = stage.getBoundingClientRect();
    calloutPanel?.focusAt({ x: e.clientX - sr.left, y: e.clientY - sr.top });
  }, { signal: ac.signal });
  // the bar and the strips never take the keyboard away from the callout or the shot being typed
  // (the alignments in the inspector likewise: a tap on one leaves the caret in the callout's fields)
  side.el.addEventListener('mousedown', (e) => {
    if (side.key()?.startsWith('align:')) e.preventDefault();
  }, { signal: ac.signal });
  for (const el of [context, subtools.el, infobar.el]) el.addEventListener('mousedown', (e) => {
    if (editingId || editor?.elevEditing) e.preventDefault();
  }, { signal: ac.signal });
  /** A colour with no name of its own leaves the selected callout's material word to be typed: straight into it. */
  function nameColourOnly(): void {
    const it = editor?.selection ? editor.getItem(editor.selection) : undefined;
    if (it?.t !== 'callout' || it.template === 'custom' || it.materialName !== '') return;
    if (editingId === it.id) calloutPanel?.focusFirst();
    else beginCalloutEdit(it.id, null);
  }

  /** What the callout's bar offers: by state, and while the alignment is typed, by what is typed. */
  let barKey = '';
  const barKeyOf = (c: Callout): string => {
    if (editor?.pointing === c.id) return 'pointing';
    if (editingId !== c.id) return `selected|${c.template === 'custom'}|${c.box ? 1 : 0}`;
    return `editing|${c.template === 'custom'}|${JSON.stringify(alignmentsFor(c))}|${c.alignment.trim()}|${calloutPanel?.canAddRange() ?? false}`;
  };
  /** The alignments to offer while the alignment is being typed: the ones used on this map first, narrowed by what is typed. */
  function alignmentsFor(c: Callout): string[] {
    const typing = calloutPanel?.alignmentTyping();
    if (!editor || editingId !== c.id || c.template === 'custom' || typing === undefined || typing === null) return [];
    const all = [...new Set([...usedAlignments(c), ...DEFAULT_ALIGNMENTS])];
    const q = typing.toLowerCase();
    return all.filter((a) => a.toLowerCase().startsWith(q));
  }
  /** The alignments this map's other callouts are on, the latest first. */
  function usedAlignments(c: Callout): string[] {
    return [...new Set(editor!.doc.items.flatMap((i) => (i.t === 'callout' && i.id !== c.id && i.alignment.trim() ? [i.alignment.trim()] : [])).reverse())];
  }
  /**
   * Every alignment at once, in the inspector (2026-10-28, Adam: suggestions "presented in this new
   * minimal and perfect way … instead of just all cluttering up the UI"): those of this map first, then
   * the usual ones, what is typed in bold, the one it is ticked; a tap takes one (and on to the stations),
   * the inspector folding away.
   */
  function openAlignments(c: Callout): void {
    const used = usedAlignments(c);
    side.open({
      key: `align:${c.id}`,
      title: 'Alignment',
      about: c.id,
      dot: calloutColor(editor!.doc, c),
      build: (body) =>
        append(body, sideChoices({
          groups: [{ label: 'On this map', items: used.map((v) => ({ value: v })) }, { label: used.length ? 'Usual' : undefined, items: DEFAULT_ALIGNMENTS.filter((a) => !used.includes(a)).map((v) => ({ value: v })) }],
          now: (editor?.getItem(c.id) as Callout | undefined)?.alignment.trim() ?? null,
          typed: calloutPanel?.alignmentTyping() ?? '',
          onPick: (a) => {
            calloutPanel?.chooseAlignment(a);
            side.close();
          },
        })),
    });
  }
  /**
   * The bar beside a callout. Selected: Edit, Arrows, Delete, More (custom text or station fields,
   * clear, automatic position). Arrows: every tap on a mark points the callout at it (or no longer
   * at it) until Done. Typed into: nothing, but for the alignments on offer while the alignment is
   * typed, and Done for free text (where Return is a new line).
   */
  function fillCalloutBar(c: Callout): void {
    const ed = editor!;
    const id = c.id;
    const text = c.template === 'custom';
    const soils = ed.doc.kind === 'soils';
    // its arrows being chosen: the strip beside the tools says so, and has Done
    if (ed.pointing === id) return;
    if (editingId === id) {
      // the alignments ready to use: every one at once, in the inspector (the field completes itself as it is typed)
      const matches = alignmentsFor(c);
      if (matches.length) context.append(chip(`Alignments · ${matches.length}`, () => (haptic(), openAlignments(c)), { icon: ICONS.ready, aria: 'Alignments' }));
      // one statement for several stretches of a line: offered once the last one has both its stations
      if (calloutPanel?.canAddRange()) context.append(chip('and between', () => (haptic(), calloutPanel?.addRange()), { icon: ICONS.plus, aria: 'Add stations' }));
      if (text) context.append(chip('Done', () => (endCalloutEdit(), renderContext()), { icon: ICONS.check, on: true }));
      return;
    }
    const iconChip = (name: string, aria: string, onClick: () => void, danger = false) => h('button', { class: 'btn icon' + (danger ? ' danger' : ''), type: 'button', 'aria-label': aria, onClick }, icon(name, 20));
    // (what the menu offers is read when it opens: the callout may have changed since the bar was built)
    const now = (): Callout => (ed.getItem(id) as Callout | undefined) ?? c;
    const more: HTMLButtonElement = iconChip(ICONS.more, 'More', () =>
      openMenu(more, [
        // a soils callout is words alone: there are no station fields to go back to
        ...(soils ? [] : [text
          ? { label: 'Use station fields', icon: ICONS.callout, onSelect: () => (ed.editCallout(id, { template: 'between' }, 'template'), beginCalloutEdit(id, null)) }
          : {
              label: 'Write custom text',
              icon: ICONS.text,
              onSelect: () => {
                const cur = ed.getItem(id);
                if (!cur || cur.t !== 'callout') return;
                ed.editCallout(id, { template: 'custom', custom: cur.custom.trim() ? cur.custom : calloutLines(cur).join('\n') }, 'template');
                beginCalloutEdit(id, null);
              },
            }]),
        {
          label: 'Clear and start over',
          icon: ICONS.undo,
          onSelect: () => {
            ed.updateItem<Callout>(id, (cur) => {
              const { materialName: _was, ranges: _ranges, auto: _auto, ...rest } = cur;
              const swatch = cur.material ? materialByKey(cur.material).swatchOnly : false;
              return { ...rest, template: soils ? 'custom' : 'between', from: '', to: '', custom: '', ...(swatch ? { materialName: '' } : {}) };
            });
            beginCalloutEdit(id, null);
          },
        },
        // stations typed over (or never read): read off the plan again, when it gives them
        ...(!soils && !now().auto && ed.canFillFromPlan(id) ? [{ label: 'Stations from the plan', icon: ICONS.callout, onSelect: () => (haptic(), ed.fillFromPlan(id)) }] : []),
        ...(now().box ? [{ label: 'Position automatically', icon: ICONS.reset, onSelect: () => ed.updateItem<Callout>(id, (cur) => ({ ...cur, box: null })) }] : []),
        // arrows put by hand go back to the middle of their marks' edges
        // (an arrow taken off — shaken off its mark — comes back with the others put back in the middle)
        ...(ed.hasPinnedArrows(id) ? [{ label: ed.hasArrowOff(id) ? 'Arrows back' : 'Arrows to the middle', icon: ICONS.arrows, onSelect: () => ed.centreArrows(id) }] : []),
      // the menu opens away from the callout, never over it
      ], { above: context.getBoundingClientRect().top < (ed.anchorRect(id)?.y ?? 0) + stage.getBoundingClientRect().top }),
    );
    // as every item's bar: its actions, ⋯, and Delete last after a hairline
    append(context, [
      // (words with a look of their own are written in the side panel: typed over in place, they would lose it)
      chip('Edit', () => (now().rich ? openTextPanel(id, 'words') : beginCalloutEdit(id, null)), { icon: ICONS.edit }),
      // its words' looks and its box's: the side panel
      chip('Text', () => openTextPanel(id, text ? 'words' : null), { icon: ICONS.text, aria: 'Text: size, alignment and looks' }),
      chip('Arrows', () => (haptic(), ed.setPointing(true)), { icon: ICONS.arrows, aria: 'Arrows: choose what it points at' }),
      more,
      h('span', { class: 'divider', 'aria-hidden': 'true' }),
      iconChip(ICONS.trash, 'Delete', () => {
        if (ed.selection !== id) ed.select(id);
        ed.deleteSelection();
      }, true),
    ]);
  }
  /** While corners are placed one at a time: what the next tap does, take the last one back, finish, or stop. */
  /** What a point is (and, placing a shape, what the next is): each a switch, the one it is lit. */
  const POINT_KINDS: [PenKind, string, string, string][] = [
    ['corner', 'Corner', ICONS.pointCorner, 'a sharp corner'],
    ['rounded', 'Rounded', ICONS.pointRounded, 'a rounded corner'],
    ['curve', 'Curve', ICONS.pointCurve, 'the outline curves through it'],
    ['draw', 'Draw', ICONS.draw, 'draw the outline on with a finger'],
  ];
  function kindChips(current: PenKind | null, pick: (k: PenKind) => void, withDraw: boolean, whose: string): HTMLElement {
    const group = h('div', { class: 'kinds', role: 'group', 'aria-label': whose });
    for (const [k, label, ic, what] of POINT_KINDS) {
      if (k === 'draw' && !withDraw) continue;
      group.append(chip(label, () => (pick(k), haptic()), { icon: ic, pressed: current === k, aria: `${label}: ${what}` }));
    }
    return group;
  }
  function snapChip(): HTMLButtonElement {
    const ed = editor!;
    return chip('Snap', () => {
      ed.snapAnchors = !ed.snapAnchors;
      void updateSettings({ snapAnchors: ed.snapAnchors });
      haptic();
      renderContext();
    }, { on: ed.snapAnchors, aria: ed.snapAnchors ? 'Snap to the plan: on' : 'Snap to the plan: off' });
  }
  /**
   * Corners being placed: what the next point is (or the selected one), snapping, the way out, Done.
   * (The last point is taken back by Undo — the one at the top, ⌘Z, two fingers — never a second
   * undo of its own.)
   */
  function buildingNodes(n: number): Node[] {
    const ed = editor!;
    const sel = ed.buildingSelection;
    const kind = sel !== null ? ed.buildingPointKind : ed.penKind;
    const done = chip('Done', () => (ed.finishBuilding(), haptic()), { icon: ICONS.check, on: n >= 3, aria: 'Done: close the shape' });
    done.disabled = n < 3;
    return [
      kindChips(kind, (k) => ed.setPenKind(k), sel === null, sel !== null ? 'This point' : 'Next point'),
      sel !== null ? chip('Delete point', () => (ed.deleteBuildingPoint(), haptic()), { icon: ICONS.close, danger: true }) : null,
      h('span', { class: 'divider', 'aria-hidden': 'true' }),
      snapChip(),
      h('button', { class: 'btn icon', type: 'button', 'aria-label': 'Stop placing points', onClick: () => (ed.cancelBuilding(), haptic()) }, icon(ICONS.close, 20)),
      done,
    ].filter((x): x is HTMLElement => x !== null);
  }
  /** The controls of the task under way (corners placed, arrows chosen, shapes cut out), for the strip; null when there is none. */
  function taskNodes(): Node[] | null {
    const ed = editor;
    if (!ed) return null;
    if (ed.building !== null) return buildingNodes(ed.building);
    if (ed.pointing) return [h('span', { class: 'hint' }, ed.doc.kind === 'soils' ? 'Tap the areas it points at' : 'Tap the marks it points at'), chip('Done', () => (ed.setPointing(false), haptic()), { icon: ICONS.check, on: true })];
    // (cutting with Shapes, the shape it cuts is chosen here too)
    if (ed.cutting) return [isShape(ed.tool) && shapeKinds, isShape(ed.tool) && h('div', { class: 'divider' }), h('span', { class: 'hint' }, 'Draw what to cut out'), chip('Done', () => (ed.setCutting(false), haptic()), { icon: ICONS.check, on: true, aria: 'Done cutting' })].filter((x): x is HTMLElement => !!x);
    return null;
  }
  let taskKey = '';
  /** The strip shows the task under way while there is one, and what the next mark will be again after. */
  function syncTaskStrip(): void {
    const ed = editor;
    if (!ed) return;
    const key = ed.building !== null ? `b|${ed.building}|${ed.buildingSelection}|${ed.penKind}|${ed.buildingPointKind}|${ed.snapAnchors}` : ed.pointing ? 'p' : ed.cutting ? `c|${ed.tool}` : '';
    if (key === taskKey) return;
    taskKey = key;
    renderSubtools();
    renderInfobar();
  }
  /** The task's controls in a strip, if there is a task (true): what the next mark will be waits until it is done. */
  function renderTask(s: ReturnType<typeof strip>): boolean {
    const nodes = taskNodes();
    if (!nodes) return false;
    const ed = editor!;
    s.morph(ed.building !== null ? 'task:build' : ed.pointing ? 'task:point' : 'task:cut', () => {
      reconcile(s.scroll, nodes);
      if (nodes.includes(shapeKinds)) fillShapes(ed);
    });
    return true;
  }
  /** A drawn shape's selected point: a corner, rounded or curving through, take it away, snapping, done. */
  function pointChips(): HTMLElement[] {
    const ed = editor!;
    const kind = ed.selectedPointKind;
    return [
      // (its points are out: Done puts them away)
      chip('Done', () => (ed.editPoints(false), haptic()), { icon: ICONS.check, on: true, aria: 'Done editing points' }),
      kind === null ? null : kindChips(kind, (k) => k !== 'draw' && ed.setSelectedPointKind(k), false, 'This point'),
      ed.vertexSelection !== null ? chip('Delete point', () => ed.deleteVertex(), { icon: ICONS.close, danger: true }) : null,
      snapChip(),
    ].filter((x): x is HTMLElement => x !== null);
  }
  function fillContextChips(sel: string, it: Item | undefined): void {
    const ed = editor!;
    const del = () => h('button', { class: 'btn icon danger', type: 'button', 'aria-label': 'Delete', onClick: () => ed.deleteSelection() }, icon(ICONS.trash, 20));
    // whether dragged anchor points snap to the plan's lines: one tap, remembered on this device
    const snapChip = () =>
      chip('Snap', () => {
        ed.snapAnchors = !ed.snapAnchors;
        void updateSettings({ snapAnchors: ed.snapAnchors });
        haptic();
        renderContext();
      }, { on: ed.snapAnchors, aria: ed.snapAnchors ? 'Snap to lines: on' : 'Snap to lines: off' });
    if (isSharedLabel(sel)) {
      // a markup's label (or the failing areas'): its words, and where it and its arrows go
      const n = markupOfLabel(sel);
      append(context, [
        chip('Edit text', () => void (n === null ? editFailLabel() : editMarkupLabel(n)), { icon: ICONS.edit }),
        ed.sharedLabelPinned(sel) ? chip('Auto position', () => (ed.autoPlaceShared(sel), haptic()), { icon: ICONS.reset }) : null,
        // its arrows put by hand (dragged along their areas) go back to the middles
        ed.sharedArrowsPinned(sel) ? chip(ed.sharedArrowsOff(sel) ? 'Arrows back' : 'Arrows to the middle', () => (ed.resetSharedArrows(sel), haptic()), { icon: ICONS.arrows }) : null,
        // taken off the map, with its arrows (its areas stay; Label on one of them brings it back)
        h('span', { class: 'divider', 'aria-hidden': 'true' }),
        del(),
      ]);
      return;
    }
    if (!it) return;
    // editing a legacy band's anchors: only what belongs to that — Done, Snap, and the selected anchor's Delete
    if ((it.t === 'hl' || it.t === 'area') && ed.editing === it.id) {
      append(context, [
        chip('Done', () => ed.setEditing(false), { icon: ICONS.check, on: true }),
        snapChip(),
        ed.vertexSelection !== null ? chip('Delete point', () => ed.deleteVertex(), { icon: ICONS.close, danger: true }) : null,
      ]);
      return;
    }
    // cutting out of a mark: what the next shapes do, the cut in hand's own controls, and the way out
    if (it.t === 'cut' || ((it.t === 'hl' || it.t === 'area') && ed.cutting === it.id)) {
      // (what the next shapes do, and Done, are in the strip beside the tools)
      if (it.t !== 'cut') return;
      // a cut in hand: its points (Edit), Snap, take it away — each shape stays editable, whenever
      if (ed.pointEditing) return void append(context, pointChips());
      append(context, [
        chip('Edit', () => (ed.editPoints(true), haptic()), { icon: ICONS.edit, aria: 'Edit points' }),
        snapChip(),
        h('div', { class: 'divider' }),
        h('button', { class: 'btn icon danger', type: 'button', 'aria-label': 'Delete cut', onClick: () => ed.deleteSelection() }, icon(ICONS.trash, 20)),
      ]);
      return;
    }
    // a shape's points out (Edit): what belongs to editing points, and Done
    if ((it.t === 'hl' || it.t === 'area') && ed.pointEditing) return void append(context, [...pointChips(), del()]);
    /*
     * Every item's bar has the same shape: its most used actions, as words with their icons — a
     * test's and a shot's Name and Note among them, a tap away (2026-10-28, the inspectors: not
     * behind ⋯) — then ⋯ for the rest — a menu whose switches show a check and whose values show at
     * their end — and Delete last, after a hairline, in red. Nothing on a bar needs explaining.
     */
    const moreMenu = (items: (MenuItem | typeof MENU_SEP | null | false)[], spilled: MenuItem[] = []) => {
      const b: HTMLButtonElement = h('button', { class: 'btn icon', type: 'button', 'aria-label': 'More', onClick: () => {
        haptic();
        // (the buttons a narrow bar has no room for head the menu)
        const out = [...context.querySelectorAll<HTMLElement>('.btn.spill')].some((x) => getComputedStyle(x).display === 'none');
        openMenu(b, [...(out ? [...spilled, MENU_SEP] : []), ...items].filter((x): x is MenuItem | typeof MENU_SEP => !!x), { above: context.getBoundingClientRect().top < (ed.anchorRect(sel, true)?.y ?? 0) + stage.getBoundingClientRect().top });
      } }, icon(ICONS.more, 20));
      return b;
    };
    /** A button on the bar that goes under ⋯ where the bar has no room for it (a phone): `moreMenu`'s `spilled`. */
    const spill = (b: HTMLElement) => (b.classList.add('spill'), b);
    const tail = (more: HTMLElement | null) => [more, h('span', { class: 'divider', 'aria-hidden': 'true' }), del()];
    const snapItem = (): MenuItem => ({ label: 'Snap to the plan', icon: ICONS.magnet, checked: ed.snapAnchors, onSelect: () => {
      ed.snapAnchors = !ed.snapAnchors;
      void updateSettings({ snapAnchors: ed.snapAnchors });
      haptic();
      renderContext();
    } });
    const cutItem = (): MenuItem => ({ label: 'Cut out', icon: ICONS.cut, onSelect: () => (ed.setCutting(true), haptic()) });
    // its points (Edit): a drawn shape's, or an outline read off the plan's
    const editChip = (x: WorkArea | Highlight): HTMLElement | null =>
      x.shape.k === 'stroke' ? chip('Edit', () => ed.setEditing(true), { icon: ICONS.edit }) : ed.canEditPoints ? chip('Edit', () => (ed.editPoints(true), haptic()), { icon: ICONS.edit, aria: 'Edit points' }) : null;
    const widthItems = (x: WorkArea | Highlight): MenuItem[] =>
      x.shape.k !== 'stroke'
        ? []
        : [
            { label: 'Thinner', icon: ICONS.thinner, onSelect: () => ed.updateItem(x.id, (y) => ((y.t === 'area' || y.t === 'hl') && y.shape.k === 'stroke' ? { ...y, shape: { ...y.shape, width: Math.max(2, y.shape.width / 1.2) } } : y)) },
            { label: 'Wider', icon: ICONS.wider, onSelect: () => ed.updateItem(x.id, (y) => ((y.t === 'area' || y.t === 'hl') && y.shape.k === 'stroke' ? { ...y, shape: { ...y.shape, width: y.shape.width * 1.2 } } : y)) },
          ];
    switch (it.t) {
      case 'test': {
        // what a test is most often marked: failing, or the retest of one
        const sand = methodOf(it) === 'sandcone';
        append(context, [
          chip('Failing', () => (ed.updateItem<TestPoint>(it.id, (t) => ({ ...t, status: t.status === 'fail' ? 'none' : 'fail' })), haptic()), { icon: ICONS.fail, pressed: it.status === 'fail', fail: true }),
          // (the test it retests, named as its badge names it: "Retest of Test #3")
          chip(it.retestOf ? `Retest of ${badgeRef(ownNameOf(ed.doc.items, it.retestOf, it), it.retestOf, style())}` : 'Retest', () => void editRetest(it), { icon: ICONS.retest }),
          // what its badge says, and the words after it
          spill(chip('Test number', () => openTextPanel(it.id, 'name'), { icon: ICONS.tag, aria: `Test number: ${badgeText(it)}` })),
          spill(chip('Note', () => openTextPanel(it.id, 'words'), { icon: ICONS.note, aria: it.note ? `Note: ${it.note}` : 'Note' })),
          ...tail(
            moreMenu(
              [
                // taken by sand cone after all (or not): it takes that method's next number
                { label: 'Sand cone', icon: ICONS.test, checked: sand, onSelect: () => (ed.setTestMethod(it.id, sand ? 'nuke' : 'sandcone', numberFloor[sand ? 'nuke' : 'sandcone']), haptic()) },
                it.label ? MENU_SEP : null,
                it.label ? { label: 'Place its label automatically', icon: ICONS.reset, onSelect: () => ed.updateItem<TestPoint>(it.id, (t) => ({ ...t, label: null })) } : null,
              ],
              [
                { label: 'Test number', icon: ICONS.tag, detail: badgeText(it), onSelect: () => openTextPanel(it.id, 'name') },
                { label: 'Note', icon: ICONS.note, onSelect: () => openTextPanel(it.id, 'words') },
              ],
            ),
          ),
        ]);
        break;
      }
      case 'area': {
        const fail = it.status === 'fail';
        const n = markupOf(it);
        // which markup the area is in, in its colour; tapped, it offers to move the area to another (or a new) one
        const c = areaColors(it.color);
        const mk: HTMLButtonElement = h('button', { class: 'btn', type: 'button', 'aria-label': `Markup ${n}: move to another`, onClick: () => {
          // (an area alone in the last markup is already its own, last markup: no new one for it)
          const alone = !ed.doc.items.some((x) => x.t === 'area' && x.id !== it.id && x.status !== 'fail' && markupOf(x) === n);
          openMover(mk, { word: 'Markup', count: ed.markupCount, own: n, canNew: !(alone && n === ed.markupCount), colour: (m) => areaColors(ed.markupColour(m)), move: (m) => ed.moveMarkup(it.id, m) });
        } }, h('span', { class: 'swatch', style: { background: c.fill, boxShadow: `inset 0 0 0 2px ${c.stroke}` } }), h('span', { class: 'label' }, `Markup ${n}`), icon(ICONS.chevronDown, 14));
        append(context, [
          editChip(it),
          fail ? chip('Failing', () => (ed.setAreaFailing(it.id, false), haptic()), { icon: ICONS.fail, pressed: true, fail: true }) : mk,
          // the words of its markup's label (or the failing areas')
          chip('Label', () => void (fail ? editFailLabel() : editMarkupLabel(n)), { icon: ICONS.text, aria: 'Label text' }),
          ...tail(
            moreMenu([
              fail ? null : { label: 'Failing area', icon: ICONS.fail, onSelect: () => (ed.setAreaFailing(it.id, true), haptic()) },
              // words of the inspector's own about this area, besides the label
              { label: 'Add a callout', icon: ICONS.callout, onSelect: () => ed.addCallout(null, centerOf(it) ?? { x: ed.doc.w / 2, y: ed.doc.h / 2 }, it.id) },
              cutItem(),
              MENU_SEP,
              ed.selectionIsShape ? snapItem() : null,
              ...widthItems(it),
            ]),
          ),
        ]);
        break;
      }
      case 'hl': {
        // which pour the mark is in; tapping it offers to move the mark to another (or a new) one
        const own = placementOf(it);
        const pc: HTMLButtonElement = chip(`Pour ${own}`, () => {
          // (a mark alone in the last pour is already its own, last pour: no new one for it)
          const alone = !ed.doc.items.some((x) => x.t === 'hl' && x.id !== it.id && placementOf(x) === own);
          openMover(pc, { word: 'Pour', count: ed.placementCount, own, canNew: !(alone && own === ed.placementCount), move: (n) => ed.movePlacement(it.id, n) });
        }, { aria: `Pour ${own}: move to another` });
        pc.append(icon(ICONS.chevronDown, 14));
        append(context, [
          editChip(it),
          pc,
          chip('Callout', () => ed.addCallout(it.material, centerOf(it) ?? { x: ed.doc.w / 2, y: ed.doc.h / 2 }, it.id), { icon: ICONS.callout }),
          ...tail(moreMenu([cutItem(), MENU_SEP, ed.selectionIsShape ? snapItem() : null, ...widthItems(it)])),
        ]);
        break;
      }
      case 'elev': {
        // being typed into: the pad is its bar (Done is on it)
        if (ed.elevEditing === it.id) break;
        const word = prefixOf(it, ed.doc.elev);
        const depth = depthText(it.value, it.ref, ed.doc.elev);
        append(context, [
          // its elevation: typed again on the pad
          chip(shownValue(it.value) || 'Type it', () => (ed.beginElevEdit(it.id), haptic()), { icon: ICONS.edit, aria: 'Edit the elevation' }),
          // measured against a grade the inspector types: the tag says how far below it
          chip(depth ?? 'Depth', () => void editElevDepth(it), { icon: ICONS.ruler, aria: depth ? `Depth: ${depth}` : 'Depth below a grade' }),
          // the words after its elevation
          spill(chip('Note', () => openTextPanel(it.id, 'words'), { icon: ICONS.note, aria: it.note ? `Note: ${it.note}` : 'Note' })),
          ...tail(
            moreMenu(
              [
                { label: 'Number', icon: ICONS.tag, detail: `#${it.n}`, onSelect: () => openTextPanel(it.id, 'number') },
                // (its own word, when the map's isn't what this one was shot on: the choice opens from the same button)
                { label: 'Word', icon: ICONS.elev, detail: word || 'None', onSelect: () => chooseElevWord(context.querySelector<HTMLElement>('[aria-label="More"]') ?? context, it.id) },
                it.label ? MENU_SEP : null,
                it.label ? { label: 'Place its tag automatically', icon: ICONS.reset, onSelect: () => ed.updateItem<Elevation>(it.id, (x) => ({ ...x, label: null })) } : null,
              ],
              [{ label: 'Note', icon: ICONS.note, onSelect: () => openTextPanel(it.id, 'words') }],
            ),
          ),
        ]);
        break;
      }
      case 'note':
        {
          const colourBtn: HTMLButtonElement = chip('Colour', () => pickNoteColor(it, colourBtn));
          context.append(chip('Edit', () => openTextPanel(it.id, 'words'), { icon: ICONS.edit }), colourBtn, del());
        }
        break;
      case 'callout':
        break;
    }
  }
  /**
   * Which pour (or markup) a selected mark (or area) is in, chosen again: the numbers in a row, its
   * own ringed, and + for a new one — a tap moves it there. Small, right by its button.
   */
  function openMover(anchor: HTMLElement, o: { word: string; count: number; own: number; canNew: boolean; colour?: (n: number) => { fill: string; stroke: string }; move: (n: number) => void }): void {
    if (o.count <= 1 && !o.canNew) return void toast(`This is the only ${o.word.toLowerCase()} on the map`);
    haptic();
    openPopover(anchor, (close) => {
      const pick = (n: number) => () => {
        close();
        if (n === o.own) return;
        o.move(n);
        haptic();
      };
      const chips: HTMLElement[] = Array.from({ length: o.count }, (_, i) => i + 1).map((n) => {
        const c = o.colour?.(n);
        return h('button', { class: 'chip num' + (c ? ' markup' : ''), type: 'button', 'aria-label': `${o.word} ${n}`, 'aria-pressed': String(n === o.own), style: c ? { '--mk-fill': c.fill, '--mk-stroke': c.stroke } : {}, onClick: pick(n) }, c ? h('span', { class: 'mk' }, String(n)) : String(n));
      });
      if (o.canNew) chips.push(h('button', { class: 'chip num new', type: 'button', 'aria-label': `New ${o.word.toLowerCase()}`, onClick: pick(o.count + 1) }, icon(ICONS.plus, 20)));
      return [h('div', { class: 'mover-title' }, `Move to ${o.word.toLowerCase()}`), h('div', { class: 'mover', role: 'group', 'aria-label': o.word }, ...chips)];
    }, { label: `Move to another ${o.word.toLowerCase()}` });
  }
  /**
   * The panel sits just above the selected item (below it when there is no room),
   * on the side away from a callout's arrow, and never over the bars.
   */
  function positionContext(): void {
    if (!editor) return;
    if (elevField) fitElevField();
    const sel = editor.selection;
    if (!sel) return;
    // a test's bar goes by its label (what was touched), however far off its dot that is
    let r = editor.anchorRect(sel, true);
    if (!r) return;
    const it = isSharedLabel(sel) ? undefined : editor.getItem(sel);
    let prefer: 'above' | 'below' = 'above';
    if (it?.t === 'callout' && editingId === it.id && calloutPanel) {
      // the editor is the callout: the same box, border, radius, text size and colour. Its
      // pieces are sized first, since the plan lays the box out from them
      const m = editor.metrics;
      // (at the callout's own size, when it was made larger or smaller)
      const vs = editor.view.scale * (editor.labelLook(sel)?.size ?? 1);
      calloutPanel.fit(m.fontCallout * vs, false);
      r = editor.anchorRect(sel) ?? r;
      const bw = m.calloutBorder * editor.view.scale;
      inplace.style.left = `${r.x - bw / 2}px`;
      inplace.style.top = `${r.y - bw / 2}px`;
      inplace.style.width = `${r.w + bw}px`;
      inplace.style.height = `${r.h + bw}px`;
      inplace.style.borderWidth = `${bw}px`;
      inplace.style.borderRadius = `${m.calloutRadius * editor.view.scale + bw / 2}px`;
      // the plan strokes the border centred on the box's edge: half of it lies inside the padding
      inplace.style.padding = `${m.pillPadY * vs - bw / 2}px ${m.pillPadX * vs - bw / 2}px`;
      inplace.style.fontSize = `${m.fontCallout * vs}px`;
      inplace.style.lineHeight = `${m.fontCallout * vs * m.lineHeight}px`;
      const color = calloutColor(editor.doc, it);
      inplace.style.setProperty('--callout-color', color);
      inplace.style.setProperty('--callout-text', darkOf(color));
      inplace.style.setProperty('--callout-tint', tintOf(color, 0.1));
      inplace.style.setProperty('--callout-tint-strong', tintOf(color, 0.22));
    }
    if (!context.classList.contains('show')) return;
    // a tag near its dot: the bar keeps clear of both, and of the leader between them
    if (it?.t === 'test' || it?.t === 'elev') {
      const all = editor.anchorRect(sel);
      if (all && all.h < r.h + 140) r = all;
    }
    // a callout's bar goes on the side away from its arrow
    if (it?.t === 'callout' && editor.calloutSide(sel) === 'top') prefer = 'below';
    // an area's panel keeps clear of the area label
    if (it?.t === 'area') {
      const al = editor.anchorRect(it.status === 'fail' ? FAIL_LABEL : markupLabelId(markupOf(it)));
      if (al && al.y + al.h > r.y - 80 && al.y < r.y + r.h + 80) r = unionRect(r, al);
    }
    const W = stageW || stage.clientWidth,
      H = stageH || stage.clientHeight;
    const room = barRoom(H);
    // the bar is placed by `translate` alone, so following the plan costs no layout
    // left of the inspector, when it is open: as compact as it goes there (Name and Note under ⋯, as on a
    // phone) where the room beside it is short — and, were it still too wide, over the inspector, never under it
    const open = !!side.key();
    const paneL = open ? side.el.offsetLeft - stage.offsetLeft : W;
    const fitFor = `${contextKey}|${barKey}|${Math.round(paneL)}|${W}`;
    if (fitFor !== barFitFor) {
      barFitFor = fitFor;
      context.classList.remove('narrow');
      context.classList.toggle('narrow', context.offsetWidth > (open ? paneL - 8 : room.r) - room.l);
      barSize = null;
    }
    barSize ??= { w: context.offsetWidth, h: context.offsetHeight };
    const pw = barSize.w,
      ph = barSize.h;
    const overSide = open && pw > paneL - 8 - room.l;
    context.classList.toggle('over-side', overSide);
    // The bar is fixed to the middle of what it acts on; it comes up over the middle of what of it can be
    // seen, just above (or below) a mark's own outline there, not its bounding box.
    const mid = r.x + r.w / 2;
    let seen = mid;
    let top = r.y,
      bottom = r.y + r.h;
    const outline = it && (it.t === 'hl' || it.t === 'area') ? editor.outlineScreen(sel) : null;
    if (outline && outline.length >= 6) {
      const span = visibleSpan(outline, W, H) ?? { l: Math.max(0, r.x), r: Math.min(W, r.x + r.w) };
      seen = (span.l + span.r) / 2;
      // (the outline under the point the bar is fixed to: it goes with the plan)
      const x = bar.anchor(mid, seen, editor.view.scale) - pw / 2;
      const ext = outlineExtent(outline, x, x + pw);
      if (ext) {
        top = ext.top;
        bottom = ext.bottom;
      }
      // and clear of its turning knob, which stands just beyond it
      const knob = editor.knobScreen(sel);
      if (knob) {
        top = Math.min(top, knob.y - 24);
        bottom = Math.max(bottom, knob.y + 24);
      }
    }
    const v = editor.view;
    const now = performance.now();
    const input: BarInput = {
      w: pw,
      h: ph,
      room,
      controls: (overSide ? room.keepOff.filter((o) => o.key !== 'side') : room.keepOff).map((o) => o.r),
      mid,
      seen,
      top,
      bottom,
      maxX: open && !overSide ? paneL - 8 - pw : Infinity,
      prefer,
      // (only a bar coming up keeps off a label: the labels are asked for then alone)
      labels: barShown ? [] : editor.labelRects(),
      key: contextKey,
      view: { scale: v.scale, tx: v.tx, ty: v.ty },
    };
    bar.place(input, now);
    barShown = true;
    const at = bar.side(input);
    // (dev: what it stands by, for scripts/bar to judge it by)
    if (import.meta.env.DEV) (globalThis as { __barAnchor?: unknown }).__barAnchor = { x: bar.anchor(mid, seen, v.scale), top, bottom, side: at, place: bar.stands };
    drawBar(now);
    context.style.transformOrigin = at === 'above' ? '50% 100%' : '50% 0%';
  }
  /** The bar where it stands, and as far off that as a glide there still has to go (drawn frame by frame till it is there). */
  function drawBar(now = performance.now()): void {
    const p = bar.drawn(now);
    // (on the device's own pixels, as the plan is drawn: never a pixel apart from it as it moves)
    const dpr = window.devicePixelRatio || 1;
    context.style.translate = `${Math.round(p.x * dpr) / dpr}px ${Math.round(p.y * dpr) / dpr}px`;
    if (p.settling && !barRaf)
      barRaf = requestAnimationFrame((t) => {
        barRaf = 0;
        if (destroyed || !context.classList.contains('show')) return;
        drawBar(t);
        // (the glass under it drawn where it now is: by the plan's own frame while the plan moves)
        if (!editor?.inMotion) refreshGlass();
      });
  }
  /** A bar shown afresh (another selection, or the same one back after it was moved): placed anew, no glide from anywhere. */
  function freshBar(): void {
    bar.reset();
    barShown = false;
  }
  /**
   * Where the bar may stand: the screen within the edges the controls keep from it, less each control
   * over the plan as it stands — grown by the room the bar keeps from it — never the strip of screen a
   * control is in. Measured when the controls or the screen change, not while the plan moves.
   */
  function barRoom(H: number): { l: number; t: number; r: number; b: number; keepOff: { key: string; r: BarRect }[] } {
    if (barRoomAt) return barRoomAt;
    const at = (el: HTMLElement): BarRect | null => {
      if (el.hidden || !el.offsetParent || !el.offsetWidth || !el.offsetHeight) return null;
      let x = 0,
        y = 0;
      for (let n: HTMLElement | null = el; n && n !== screen; n = n.offsetParent as HTMLElement | null) {
        x += n.offsetLeft;
        y += n.offsetTop;
      }
      return { x, y, w: el.offsetWidth, h: el.offsetHeight };
    };
    const m = 8;
    const keepOff: { key: string; r: BarRect }[] = [];
    const controls: [string, HTMLElement][] = [
      ['back', backBtn],
      ['title', title],
      ...[...topChrome.querySelectorAll<HTMLElement>('.cluster.trail > *')].map((el, i): [string, HTMLElement] => [`trail${i}`, el]),
      ['tools', toolbar],
      ['strip', subtools.el],
      ['info', infobar.el],
      ['zoom', zoomer],
      ['side', side.key() ? side.el : side.bubble],
    ];
    for (const [key, el] of controls) {
      const r = at(el);
      if (r) keepOff.push({ key, r: { x: r.x - m, y: r.y - m, w: r.w + 2 * m, h: r.h + 2 * m } });
    }
    // (the edges the controls stand off the screen's: the bar keeps them too)
    const l = topChrome.offsetLeft,
      t = topChrome.offsetTop;
    const dk = at(dock);
    barRoomAt = { l, t, r: l + topChrome.offsetWidth, b: dk ? dk.y + dk.h : H - t, keepOff };
    return barRoomAt;
  }

  /** The lowest and highest points of a screen polygon within a horizontal span (its edges clipped to the span). */
  function outlineExtent(poly: number[], x0: number, x1: number): { top: number; bottom: number } | null {
    const n = poly.length / 2;
    let top = Infinity,
      bottom = -Infinity;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const ax = poly[2 * i]!,
        ay = poly[2 * i + 1]!,
        bx = poly[2 * j]!,
        by = poly[2 * j + 1]!;
      const lo = Math.min(ax, bx),
        hi = Math.max(ax, bx);
      if (hi < x0 || lo > x1) continue;
      // the y values where the edge enters and leaves the span
      const yAt = (x: number) => (Math.abs(bx - ax) < 1e-9 ? ay : ay + ((by - ay) * (x - ax)) / (bx - ax));
      const ys = [yAt(Math.max(lo, x0)), yAt(Math.min(hi, x1))];
      if (ax >= x0 && ax <= x1) ys.push(ay);
      if (bx >= x0 && bx <= x1) ys.push(by);
      for (const y of ys) {
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
      }
    }
    return top === Infinity ? null : { top, bottom };
  }

  /* ---------------------------------------------- callout panel (inline, no sheet) */

  interface CalloutPanel {
    el: HTMLElement;
    sync(c: Callout): void;
    /**
     * Size every piece to its text with the plan's own measurer, at the given screen font size
     * (the last one when omitted), and hand the lines to the plan; when that changes the box,
     * the editor is placed over it again (unless the caller is doing just that: `place` false).
     */
    fit(size?: number, place?: boolean): void;
    /** The lines as shown, piece by piece (placeholders and the completion included): what the plan sizes the box from. */
    lines(): string[][];
    focusFirst(): void;
    /** Type into the field nearest a point of the stage (screen px). */
    focusAt(p: Vec): void;
    /** Take an alignment from the bar, and move on to the first station still missing. */
    chooseAlignment(a: string): void;
    /** What has been typed into the alignment since it took focus ('' before anything is); null when it is not being typed into. */
    alignmentTyping(): string | null;
    /** Whether another stretch may follow: the last one has both its stations (and there is room). */
    canAddRange(): boolean;
    /** One more line, "and between [alignment] stations [from] and [to]", on the same alignment; its first station takes the caret. */
    addRange(): void;
  }
  /**
   * The callout, typed into in place, as it reads on the plan: "[TC] placed between" over
   * "[SC2] stations [101+10] and [101+55]" — each bracket a field drawn as the callout's own
   * text on a faint pill, sized to what it holds. Stations are masked as they are typed and
   * deleted (see `maskStation`); the alignment completes itself from the ones used so far
   * (Return takes the completion); free text replaces the lines with one text area. Further
   * stretches ("and between SC1 stations 175+73 and 175+90") are lines of the same fields. Every
   * keystroke updates the plan, and the plan sizes the box from these very lines.
   */
  function buildCalloutPanel(c0: Callout): CalloutPanel {
    const ed = editor!;
    const id = c0.id;
    const current = (): Callout | null => {
      const it = ed.getItem(id);
      return it && it.t === 'callout' ? it : null;
    };
    // every piece is sized by the same measurer that lays the callout out on the plan, at the
    // screen's font size: the editor's lines are the drawn lines, to the pixel
    const meas = sharedMeasurer();
    let lastSize = 16;
    const widthOf = (text: string, size = lastSize): number => meas.width(text || ' ', 600, size);
    type Field = { input: HTMLInputElement; ghost: HTMLElement; wrap: HTMLElement };
    const field = (cls: string, aria: string, placeholder: string, extra: Record<string, unknown> = {}): Field => {
      const input = h('input', { class: `in ${cls}`, type: 'text', 'aria-label': aria, placeholder, autocorrect: 'off', autocomplete: 'off', spellcheck: false, ...extra });
      const ghost = h('span', { class: 'ghost', 'aria-hidden': 'true' });
      const wrap = h('span', { class: 'f' }, ghost, input);
      return { input, ghost, wrap };
    };
    const mat = field('mat', 'Material', 'Material', { autocapitalize: 'characters', enterkeyhint: 'next' });
    const align = field('align', 'Alignment', 'Alignment', { autocapitalize: 'characters', enterkeyhint: 'next' });
    // a station's placeholder is a station: the box is already the size it will be
    const from = field('station', 'From station', '00+00', { inputmode: 'decimal', enterkeyhint: 'next' });
    const to = field('station', 'To station', '00+00', { inputmode: 'decimal', enterkeyhint: 'done' });
    // (stations are numbers: the pad, its plus beside Return)
    for (const f of [from, to]) padField(f.input, 'num', { key: '+' });
    const word = h('span', { class: 'p' }, ' placed between');
    const pStations = h('span', { class: 'p' }, ' stations ');
    const pAnd = h('span', { class: 'p' }, ' and ');
    const line1 = h('div', { class: 'line' }, mat.wrap, word);
    const line2 = h('div', { class: 'line' }, align.wrap, pStations, from.wrap, pAnd, to.wrap);
    const custom = h('textarea', { class: 'in text', rows: 2, 'aria-label': 'Callout text', placeholder: 'Callout text', autocorrect: 'off', spellcheck: false, hidden: true });
    const el = h('div', { class: 'callout-lines' }, line1, line2, custom);

    // further stretches, each a line of its own: "and between [SC1] stations [175+73] and [175+90]"
    type Extra = { line: HTMLElement; words: HTMLElement[]; align: Field; from: Field; to: Field };
    const extras: Extra[] = [];
    const allFields = (): Field[] => [mat, align, from, to, ...extras.flatMap((e) => [e.align, e.from, e.to])];
    for (const f of [mat, align, from, to]) f.input.addEventListener('scroll', () => (f.input.scrollLeft = 0));
    const patch = (p: Parameters<typeof ed.editCallout>[1], f: string) => ed.editCallout(id, p, f);
    const patchRange = (k: number, key: keyof CalloutRange, v: string) => {
      const ranges = (current()?.ranges ?? []).map((r) => ({ ...r }));
      if (!ranges[k]) return;
      ranges[k]![key] = v;
      patch({ ranges }, `ranges:${k}:${key}`);
    };
    let completion = '';
    let alignTyped: string | null = null;
    const candidates = () => [...new Set([...ed.doc.items.flatMap((i) => (i.t === 'callout' && i.id !== id && i.alignment.trim() ? [i.alignment.trim()] : [])).reverse(), ...DEFAULT_ALIGNMENTS])];
    const shownIn = (f: Field): string => (f === align ? f.input.value + completion : f.input.value) || f.input.placeholder;
    const extraPieces = (e: Extra): string[] => [e.words[0]!.textContent ?? '', shownIn(e.align), e.words[1]!.textContent ?? '', shownIn(e.from), e.words[2]!.textContent ?? '', shownIn(e.to)];
    const lines = (): string[][] => {
      if (!custom.hidden) return (custom.value || custom.placeholder).split('\n').map((l) => [l]);
      const l2: string[] = [];
      for (const n of [align.wrap, pStations, from.wrap, pAnd, to.wrap]) {
        if (n.hidden) continue;
        const f = [align, from, to].find((x) => x.wrap === n);
        l2.push(f ? shownIn(f) : (n.textContent ?? ''));
      }
      return [[shownIn(mat), word.textContent ?? ''], l2, ...extras.map(extraPieces)];
    };
    const fit = (size = lastSize, place = true) => {
      lastSize = size;
      // (every width written first, only where it changed, and then every scroll put back: one
      // layout for a keystroke, not one per field)
      const setWidth = (el: HTMLElement, w: number) => {
        const px = `${w}px`;
        if (el.style.width !== px) el.style.width = px;
      };
      for (const p of [word, pStations, pAnd, ...extras.flatMap((e) => e.words)]) setWidth(p, widthOf(p.textContent ?? ''));
      // A field is as wide as its text plus room for the caret (taken back by a negative margin, so
      // the line is laid out by the text alone): with no room Safari scrolls the text a caret's
      // width to the left, and having scrolled — a digit arrives before the field is widened — it
      // stays scrolled. The first digit is never cut: no scroll is ever needed, and none is kept.
      const fields = allFields();
      for (const f of fields) setWidth(f.input, widthOf(shownIn(f)) + CARET_ROOM);
      for (const f of fields) if (f.input.scrollLeft) f.input.scrollLeft = 0;
      if (!custom.hidden) {
        const ls = (custom.value || custom.placeholder).split('\n');
        custom.rows = ls.length;
        custom.style.width = `${Math.ceil(Math.max(...ls.map((l) => widthOf(l)))) + 2}px`;
      }
      // the plan's box is these lines
      if (ed.setCalloutDraft({ id, lines: lines() }) && place) positionContext();
    };
    const focusInput = (input: HTMLInputElement | HTMLTextAreaElement) => {
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    };
    const showCompletion = () => {
      let v = align.input.value;
      const hit = v ? candidates().find((a) => a.toLowerCase().startsWith(v.toLowerCase())) : undefined;
      if (hit && hit.slice(0, v.length) !== v && document.activeElement === align.input) {
        // typed in the wrong case: take the known alignment's own
        v = hit.slice(0, v.length);
        align.input.value = v;
        align.input.setSelectionRange(v.length, v.length);
        patch({ alignment: v.slice(0, 60) }, 'alignment');
      }
      completion = hit && document.activeElement === align.input ? hit.slice(v.length) : '';
      align.ghost.textContent = completion ? v + completion : '';
      align.ghost.style.visibility = completion ? 'visible' : 'hidden';
      fit();
    };
    const acceptCompletion = (): boolean => {
      if (!completion) return false;
      const v = align.input.value + completion;
      align.input.value = v;
      completion = '';
      patch({ alignment: v.slice(0, 60) }, 'alignment');
      showCompletion();
      return true;
    };
    // stations: whatever is typed or deleted, the field reads as the station it will print
    const stationInput = (f: Field, write: (v: string) => void) => {
      const set = (raw: string, caret: number, deleting: boolean) => {
        const after = stationCharsAfter(raw, caret);
        const v = maskStation(raw, deleting);
        f.input.value = v;
        if (document.activeElement === f.input) {
          const at = stationCaret(v, after);
          f.input.setSelectionRange(at, at);
        }
        write(v);
        fit();
      };
      // the plus is not a character of its own: deleting across it takes the digit on its far side
      f.input.addEventListener('beforeinput', (e: Event) => {
        const type = (e as InputEvent).inputType;
        const v = f.input.value;
        const a = f.input.selectionStart ?? v.length;
        if (a !== (f.input.selectionEnd ?? a)) return;
        if (type === 'deleteContentBackward' && v[a - 1] === '+' && a >= 2) {
          e.preventDefault();
          set(v.slice(0, a - 2) + v.slice(a - 1), a - 2, true);
        } else if (type === 'deleteContentForward' && v[a] === '+' && a + 1 < v.length) {
          e.preventDefault();
          set(v.slice(0, a + 1) + v.slice(a + 2), a + 1, true);
        }
      });
      f.input.addEventListener('input', (e: Event) => set(f.input.value, f.input.selectionStart ?? f.input.value.length, ((e as InputEvent).inputType ?? '').startsWith('delete')));
    };
    stationInput(from, (v) => patch({ from: v }, 'from'));
    stationInput(to, (v) => patch({ to: v }, 'to'));
    mat.input.addEventListener('input', () => {
      patch({ materialName: mat.input.value.slice(0, 40) }, 'materialName');
      fit();
    });
    align.input.addEventListener('input', () => {
      alignTyped = align.input.value;
      patch({ alignment: align.input.value.slice(0, 60) }, 'alignment');
      showCompletion();
    });
    align.input.addEventListener('focus', () => {
      alignTyped = '';
      showCompletion();
    });
    align.input.addEventListener('blur', () => {
      alignTyped = null;
      align.ghost.style.visibility = 'hidden';
      completion = '';
      fit();
    });
    custom.addEventListener('input', () => {
      patch({ custom: custom.value.slice(0, 240) }, 'custom');
      fit();
    });
    const onKeys = (input: HTMLInputElement | HTMLTextAreaElement, following: HTMLInputElement | (() => HTMLInputElement | null) | null) => {
      input.addEventListener('keydown', (e: Event) => {
        const ke = e as KeyboardEvent;
        const k = ke.key;
        if (input === align.input && (k === 'Tab' || k === 'ArrowRight') && completion && input.selectionEnd === input.value.length) {
          e.preventDefault();
          acceptCompletion();
        } else if (k === 'Enter' && (input !== custom || ke.metaKey || ke.ctrlKey)) {
          // Return moves on, and finishes after the last field; in free text it is a new line
          e.preventDefault();
          if (input === align.input) acceptCompletion();
          const next = typeof following === 'function' ? following() : following;
          if (next) focusInput(next);
          else input.blur();
        } else if (k === 'Escape') {
          e.preventDefault();
          input.blur();
        }
      });
    };
    onKeys(mat.input, align.input);
    onKeys(align.input, from.input);
    onKeys(from.input, to.input);
    onKeys(to.input, () => extras[0]?.from.input ?? null);
    onKeys(custom, null);
    const makeExtra = (k: number): Extra => {
      const e: Extra = {
        line: h('div', { class: 'line' }),
        words: [h('span', { class: 'p' }, 'and between '), h('span', { class: 'p' }, ' stations '), h('span', { class: 'p' }, ' and ')],
        align: field('align', `Alignment ${k + 2}`, 'Alignment', { autocapitalize: 'characters', enterkeyhint: 'next' }),
        from: field('station', `From station ${k + 2}`, '00+00', { inputmode: 'decimal', enterkeyhint: 'next' }),
        to: field('station', `To station ${k + 2}`, '00+00', { inputmode: 'decimal', enterkeyhint: 'next' }),
      };
      for (const f of [e.from, e.to]) padField(f.input, 'num', { key: '+' });
      e.line.append(e.words[0]!, e.align.wrap, e.words[1]!, e.from.wrap, e.words[2]!, e.to.wrap);
      for (const f of [e.align, e.from, e.to]) f.input.addEventListener('scroll', () => (f.input.scrollLeft = 0));
      e.align.input.addEventListener('input', () => {
        patchRange(k, 'alignment', e.align.input.value.slice(0, 60));
        fit();
      });
      stationInput(e.from, (v) => patchRange(k, 'from', v));
      stationInput(e.to, (v) => patchRange(k, 'to', v));
      onKeys(e.align.input, e.from.input);
      onKeys(e.from.input, e.to.input);
      onKeys(e.to.input, () => extras[k + 1]?.from.input ?? null);
      return e;
    };
    const sync = (c: Callout) => {
      const active = document.activeElement;
      const setIf = (input: HTMLInputElement | HTMLTextAreaElement, v: string) => {
        if (active !== input && input.value !== v) input.value = v;
      };
      setIf(mat.input, calloutMaterialName(c));
      setIf(align.input, c.alignment);
      setIf(from.input, c.from);
      setIf(to.input, c.to);
      setIf(custom, c.custom);
      const ranges = c.template === 'custom' ? [] : (c.ranges ?? []);
      while (extras.length > ranges.length) extras.pop()!.line.remove();
      while (extras.length < ranges.length) {
        const e = makeExtra(extras.length);
        extras.push(e);
        el.insertBefore(e.line, custom);
      }
      ranges.forEach((r, k) => {
        const e = extras[k]!;
        setIf(e.align.input, r.alignment);
        setIf(e.from.input, r.from);
        setIf(e.to.input, r.to);
      });
      // one station reads "placed at … station 101+40", as it will print — until a station is
      // being typed, when the second field is there to take the other end
      const single = (c.from.trim() === '') !== (c.to.trim() === '') && active !== from.input && active !== to.input;
      word.textContent = single ? ' placed at' : ' placed between';
      pStations.textContent = single ? ' station ' : ' stations ';
      pAnd.hidden = single;
      from.wrap.hidden = single && c.from.trim() === '';
      to.wrap.hidden = single && c.to.trim() === '';
      const text = c.template === 'custom';
      line1.hidden = text;
      line2.hidden = text;
      custom.hidden = !text;
      fit();
    };
    sync(c0);
    return {
      el,
      sync,
      fit,
      lines,
      focusFirst: () => {
        const c = current();
        if (!c) return;
        if (c.template === 'custom') focusInput(custom);
        else if (c.materialName === '') focusInput(mat.input);
        else if (!c.alignment.trim()) focusInput(align.input);
        else focusInput(!c.from ? from.input : to.input);
      },
      focusAt: (p) => {
        if (!custom.hidden) return focusInput(custom);
        const sr = stage.getBoundingClientRect();
        let best: HTMLInputElement | null = null;
        let bd = Infinity;
        for (const f of allFields()) {
          if (f.wrap.hidden) continue;
          const r = f.input.getBoundingClientRect();
          const dx = Math.max(r.left - sr.left - p.x, 0, p.x - (r.right - sr.left));
          const dy = Math.max(r.top - sr.top - p.y, 0, p.y - (r.bottom - sr.top));
          const d = Math.hypot(dx, dy);
          if (d < bd) {
            bd = d;
            best = f.input;
          }
        }
        if (best && document.activeElement !== best) focusInput(best);
      },
      chooseAlignment: (a) => {
        align.input.value = a;
        completion = '';
        align.ghost.style.visibility = 'hidden';
        patch({ alignment: a }, 'alignment');
        const next = !from.input.value ? from.input : !to.input.value ? to.input : null;
        if (next) focusInput(next);
        else fit();
      },
      alignmentTyping: () => alignTyped,
      canAddRange: () => {
        const c = current();
        if (!c || c.template === 'custom' || (c.ranges?.length ?? 0) >= 8) return false;
        const last = c.ranges?.[c.ranges.length - 1] ?? c;
        return last.from.trim() !== '' && last.to.trim() !== '';
      },
      addRange: () => {
        const c = current();
        if (!c || c.template === 'custom') return;
        const ranges = (c.ranges ?? []).map((r) => ({ ...r }));
        // usually further along the same line
        ranges.push({ alignment: (ranges[ranges.length - 1] ?? c).alignment, from: '', to: '' });
        patch({ ranges }, 'ranges:add');
        const now = current();
        if (now) sync(now);
        const e = extras[ranges.length - 1];
        if (e) focusInput(e.from.input);
      },
    };
  }

  /**
   * With the on-screen keyboard up the visual viewport shrinks: keep the callout (or the shot being
   * typed) and its bar above the keyboard by shifting the plan, and undo any page scroll Safari
   * does to reveal the field (the app is a fixed layer; the plan pans instead).
   */
  let keeping = false;
  function keepPanelVisible(): void {
    const vv = window.visualViewport;
    if (!editor || !vv || keeping || (!calloutPanel && !elevField)) return;
    const active = document.activeElement;
    if (!active || !(context.contains(active) || inplace.contains(active) || elevField?.el.contains(active))) return;
    keeping = true;
    try {
      if (vv.offsetTop > 0 || window.scrollY > 0) window.scrollTo(0, 0);
      const sel = editor.selection;
      const stageTop = stage.getBoundingClientRect().top;
      const pr = context.classList.contains('show') ? context.getBoundingClientRect() : null;
      const ar = sel ? editor.anchorRect(sel, true) : null;
      const bottom = Math.max(pr ? pr.bottom : 0, ar ? ar.y + ar.h + stageTop : 0);
      const limit = vv.height - 12;
      if (bottom > limit) editor.nudge(0, limit - bottom);
    } finally {
      keeping = false;
    }
  }
  window.visualViewport?.addEventListener('resize', keepPanelVisible, { signal: ac.signal });
  window.visualViewport?.addEventListener('scroll', keepPanelVisible, { signal: ac.signal });

  /* ------------------------------------------------------------ sheets */

  /**
   * How the map's tests are labelled — every label changes at once, and the style is the one new maps
   * start with — the number the next test takes, and, for the style that shows them (20260925_RB_6),
   * the date and initials tests are placed with: a page of the inspector.
   */
  function editLabels(): void {
    openInspector(null, 'map', 'labels');
  }
  function labelsSection(body: HTMLElement, live: (fn: () => void) => void): void {
    const ed = editor!;
    const method = ed.testMethod;
    const d = ed.doc;
    const cur = ed.nextNumber(method, numberFloor[method]);
    const now: TestStyle = d.soils.testStyle ?? 'test';
    // a style chosen: every label changes at once (on this map, and as Settings says on every other)
    const pick = (v: TestStyle) => {
      live(() => {
        if (v !== (ed.doc.soils.testStyle ?? 'test')) ed.patchDoc({ soils: { ...ed.doc.soils, testStyle: v } });
      });
      if (v !== app.state.settings.testStyle) void updateSettings({ testStyle: v });
      void restyleTests(v, id);
      renderInfobar();
      side.refill();
    };
    const num = padField(h('input', { class: 'input mono', type: 'text', inputmode: 'numeric', value: String(cur), 'aria-label': 'Next test number', enterkeyhint: 'done' }), 'num', { point: false });
    onCommit(num, () => {
      const n = Math.floor(Number(num.value));
      const was = ed.nextNumber(method, numberFloor[method]);
      if (n === was) return;
      if (!Number.isFinite(n) || n < 1 || n > 9999) {
        num.value = String(was);
        return toast('Enter a number from 1 to 9999', 'error');
      }
      live(() => ed.patchDoc({ soils: { ...ed.doc.soils, start: { ...ed.doc.soils.start, [method]: n } } }));
      renderInfobar();
    });
    const date = h('input', { class: 'input', type: 'date', value: yyyymmddToIso(d.soils.date), 'aria-label': 'Test date' });
    const initials = h('input', { class: 'input mono', type: 'text', value: d.soils.initials, maxlength: 4, autocapitalize: 'characters', autocomplete: 'off', 'aria-label': 'Initials' });
    const setId = () => {
      const ymd = date.value.replace(/-/g, '');
      const ini = initials.value.trim().toUpperCase();
      if (!/^\d{8}$/.test(ymd) || !/^[A-Z]{1,4}$/.test(ini)) return toast(!/^\d{8}$/.test(ymd) ? 'Choose a date' : 'Initials must be 1–4 letters', 'error');
      if (ymd !== ed.doc.soils.date || ini !== ed.doc.soils.initials) live(() => ed.patchDoc({ soils: { ...ed.doc.soils, date: ymd, initials: ini } }));
      renderInfobar();
    };
    date.addEventListener('change', setId);
    onCommit(initials, setId);
    append(body, [
      // every style at once, the one in use ticked
      h('div', { class: 'side-section' }, ...sideChoices({ groups: [{ label: "Each test's badge says", items: TEST_STYLES.map((v) => ({ value: v, text: testName({ date: d.soils.date, initials: d.soils.initials, n: cur }, v) })) }], now, onPick: (v) => pick(v as TestStyle) }), sideHelp('And so do the legend, the bar and a retest — on this map and every other, as Settings says.')),
      // the date and initials are only in the labels of the style that shows them: only then are they asked for
      now === 'id' ? section('id', 'Placed with', sideRows(sideField('Date', date), sideField('Initials', initials)), sideHelp('For the tests placed from now on; the tests already placed keep theirs.')) : null,
      section('next', null, sideRows(sideField(method === 'sandcone' ? 'Next sand cone test' : 'Next test number', num)), sideHelp("Nuke and sand cone are numbered separately. A deleted test frees its number; a new map carries on from today's other maps.")),
    ]);
  }
  /** The map's style for its test badges, and what a test's badge says in it ("Test #4", "94R"). */
  const style = (): TestStyle => editor?.doc.soils.testStyle ?? 'test';
  const badgeText = (t: Pick<TestPoint, 'n' | 'name'>): string => {
    const b = badgeName(t, style());
    return b.pre + b.n;
  };
  function editRetest(t: TestPoint): void {
    openInspector(t.id, 'part', 'retest');
  }
  /**
   * A test's retest: of which test — its number or its name typed, or one of the failing tests, every
   * one of them at once, nearest first — and, once it is one, its result and how its badge then reads.
   */
  function retestSection(t: TestPoint, live: (fn: () => void) => void, done: () => void, alone: boolean): HTMLElement[] {
    const ed = editor!;
    const cur = () => ed.getItem(t.id) as TestPoint | undefined;
    // (a test's number, or its own name: numbers mostly, letters a key away)
    const num = padField(h('input', { class: 'input mono', type: 'text', autocapitalize: 'characters', autocomplete: 'off', enterkeyhint: 'done', value: t.retestOf ? nameOfNumber(ed.doc.items, t.retestOf, t) : '', placeholder: 'None', 'aria-label': 'Retest of test' }), 'mixed', { point: false });
    // the original test by its number, or by its own name (a test named "94R" is the retest of the one named "94" … or numbered 94)
    const setTo = (typed: string) => {
      if (!typed) return;
      const named = ed.doc.items.find((i): i is TestPoint => i.t === 'test' && i.id !== t.id && methodOf(i) === methodOf(t) && (i.name ?? '').toLowerCase() === typed.toLowerCase());
      const n = named ? named.n : /^\d+$/.test(typed) ? Number(typed) : NaN;
      if (!Number.isFinite(n) || n < 1 || n > 9999) return toast("Enter the original test's number (1–9999) or name", 'error');
      const now = cur();
      if (now && now.retestOf !== n) live(() => ed.updateItem<TestPoint>(t.id, (x) => ({ ...x, retestOf: n, status: x.status === 'fail' ? 'fail' : 'pass' })));
    };
    onCommit(num, () => setTo(num.value.trim()));
    // the tests that failed (of its method, nearest first): the one retested is usually one of them, a tap away
    const failed = ed.doc.items
      .filter((i): i is TestPoint => i.t === 'test' && i.id !== t.id && i.status === 'fail' && i.retestOf === null && methodOf(i) === methodOf(t))
      .sort((a, b) => Math.hypot(a.p.x - t.p.x, a.p.y - t.p.y) - Math.hypot(b.p.x - t.p.x, b.p.y - t.p.y));
    const out: (HTMLElement | null)[] = [section('of', null, sideRows(sideField(methodOf(t) === 'sandcone' ? 'Retest of sand cone' : 'Retest of test', num)))];
    if (failed.length)
      out.push(h('div', { class: 'side-section', dataset: { part: 'failing' } }, ...sideChoices({ groups: [{ label: 'Failing tests', items: failed.map((f) => ({ value: f.name ?? String(f.n), text: badgeText(f), detail: f.note.trim() || undefined })) }], now: t.retestOf ? nameOfNumber(ed.doc.items, t.retestOf, t) : null, onPick: (v) => ((num.value = v), setTo(v)) })));
    if (t.retestOf) {
      const now = cur() ?? t;
      out.push(
        section('result', 'Its result', segmented<'pass' | 'fail'>([['pass', 'Passing'], ['fail', 'Failing']], now.status === 'fail' ? 'fail' : 'pass', (v) => live(() => ed.updateItem<TestPoint>(t.id, (x) => ({ ...x, status: v })))), sideHelp(`Its badge reads “${badgeText(now)} · Retest of ${badgeRef(ownNameOf(ed.doc.items, now.retestOf!, now), now.retestOf!, style())}”; a report calls it “${testLabelText({ ...now, note: '' }, ed.doc.soils.testStyle ?? 'test', ed.doc.items)}”.`)),
        sideRows(sideRow({ icon: ICONS.close, label: 'Not a retest', danger: true, onClick: () => (live(() => ed.updateItem<TestPoint>(t.id, (x) => ({ ...x, retestOf: null, status: x.status === 'fail' ? 'fail' : 'none' }))), done()) })),
      );
    }
    // (on its own, with no failing tests to tap: straight into the field)
    if (alone && !failed.length && !t.retestOf) requestAnimationFrame(() => num.focus({ preventScroll: true }));
    return out.filter((x): x is HTMLElement => !!x);
  }
  /* ------------------------------------------------------- the inspector */

  /**
   * What the inspector (ui/side.ts) shows (2026-10-28, Adam: a bubble that "shows that either nothing
   * is selected or ALL the changeable things on whatever is currently selected … when 'Edit text' or
   * Name, Note, etc is clicked the bubble opens to just the settings relevant to what was clicked and
   * also a button to expand"). `all`: everything that can be changed about what is selected, or that
   * nothing is — opened from its pill; open, it follows the selection. `part`: one part of it (a
   * test's number, its note, a label's words …), opened from the selection's bar, with All settings at
   * its foot; it follows the selection to the same part of the next thing that has one, else goes back
   * into its pill. `map`: a setting of the map's own (its test labels, its elevations). A row may lead
   * on to a page of its own (a retest, a depth, all the words ready to use), slid in over it with a way
   * back. A part's changes are one undo step (`live`); a switch, a step of its own.
   */
  type Part = 'words' | 'name' | 'number' | 'retest' | 'depth' | 'word' | 'labels' | 'elevs';
  type Focus = 'words' | 'name' | 'number' | null;
  let insp: { mode: 'all' | 'part' | 'map'; part: Part | null; about: string | null; sig: string; seq: number } | null = null;
  let inspSeq = 0;
  /** While it is being opened: the selection it makes is its own doing, not one to follow. */
  let inspOpening = false;
  /** What the page being built keeps in step with the plan (its fields, its values) … */
  let inspSyncs: (() => void)[] = [];
  /** … and does as it lets go of what it is about (a name typed and not yet taken, a note left empty). */
  let inspLeaves: (() => void)[] = [];
  /** The words being written in it, while they are. */
  let sideText: { id: string; field: RichField | null } | null = null;

  /** The parts of a thing its bar opens on their own. */
  function partsOf(id: string): Part[] {
    if (isSharedLabel(id)) return ['words'];
    const it = editor?.getItem(id);
    if (it?.t === 'test') return ['name', 'words', 'retest'];
    if (it?.t === 'elev') return ['words', 'number', 'depth', 'word'];
    if (it?.t === 'callout' || it?.t === 'note') return ['words'];
    return [];
  }
  /** What a thing is called at the head of the inspector, and its colour. */
  function titleOf(id: string | null): { title: string; dot?: string } {
    const ed = editor;
    if (!ed || !id) return { title: 'Nothing selected' };
    const d = ed.doc;
    if (isSharedLabel(id)) return { title: id === FAIL_LABEL ? 'Failing areas’ label' : `Markup ${markupOfLabel(id) ?? 1} label`, dot: sharedLabelColor(d, id) };
    const it = ed.getItem(id);
    switch (it?.t) {
      case 'test':
        return { title: badgeText(it), dot: it.status === 'fail' ? FAIL : PASS };
      case 'elev':
        return { title: `Shot #${it.n}`, dot: elevColor(d.elev?.color) };
      case 'callout':
        return { title: 'Callout', dot: calloutColor(d, it) };
      case 'note':
        return { title: 'Note', dot: noteColor(it.color) };
      case 'area':
        return it.status === 'fail' ? { title: 'Failing area', dot: FAIL } : { title: `Markup ${markupOf(it)}`, dot: areaColors(it.color).stroke };
      case 'hl':
        return { title: `Pour ${placementOf(it)}`, dot: materialColor(it.material) };
      case 'cut':
        return { title: 'Cut out' };
      default:
        return { title: 'Nothing selected' };
    }
  }
  /**
   * What about a thing would change what the inspector holds for it — which settings it has, what
   * they are now — but not its words, its looks or where it stands: those its fields keep in step.
   */
  function inspectorSig(id: string | null): string {
    const ed = editor;
    if (!ed) return '';
    const base = `${ed.doc.kind}|${ed.snapAnchors}|${ed.markupCount}|${ed.placementCount}|${ed.doc.soils.testStyle ?? 'test'}|${ed.doc.elev?.prefix ?? ''}|${ed.doc.soils.date}|${ed.doc.soils.initials}|${JSON.stringify(ed.doc.soils.start ?? {})}|${JSON.stringify(ed.doc.elev ?? {})}`;
    if (!id) return base;
    if (isSharedLabel(id)) return `${base}|${ed.sharedLabelPinned(id)}|${ed.sharedArrowsPinned(id)}|${ed.sharedArrowsOff(id)}`;
    const it = ed.getItem(id);
    switch (it?.t) {
      case 'test':
        return `${base}|${it.status}|${it.retestOf}|${methodOf(it)}|${!!it.label}|${it.n}|${it.name ?? ''}`;
      case 'elev':
        return `${base}|${it.ref?.name ?? ''}|${it.ref?.value ?? ''}|${it.prefix ?? ''}|${!!it.label}|${it.n}`;
      case 'callout':
        return `${base}|${it.template}|${!!it.box}|${ed.hasPinnedArrows(id)}|${ed.hasArrowOff(id)}|${!!it.auto}|${editingId === id}`;
      case 'note':
        return `${base}|${it.color}`;
      case 'area':
        return `${base}|${it.status}|${markupOf(it)}|${it.color}|${it.shape.k}|${ed.canEditPoints}`;
      case 'hl':
        return `${base}|${placementOf(it)}|${it.shape.k}|${ed.canEditPoints}`;
      case 'cut':
        return `${base}|${ed.canEditPoints}`;
      default:
        return `${base}|gone`;
    }
  }
  /**
   * The inspector opened on `about` (null: nothing selected, or the map): all of it, one part, or a
   * setting of the map's; `focus` the field typed in at once (none: the keyboard waits for a tap).
   */
  function openInspector(about: string | null, mode: 'all' | 'part' | 'map', part: Part | null = null, focus: Focus = null): void {
    const ed = editor;
    if (!ed) return;
    // open on all of it already: the part is there — to it, and into its field
    if (mode === 'part' && insp?.mode === 'all' && insp.about === about && side.key() && side.depth() === 1) {
      side.el.querySelector<HTMLElement>(`[data-part="${part}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (focus) focusField(focus);
      return;
    }
    inspOpening = true;
    try {
      if (about && mode !== 'map' && ed.selection !== about) ed.select(about);
      // (a callout typed in place is done first, when its words are opened here: they are the inspector's now)
      if (about && editingId === about && mode === 'part') endCalloutEdit();
    } finally {
      inspOpening = false;
    }
    const seq = ++inspSeq;
    insp = { mode, part, about: mode === 'map' ? null : about, sig: inspectorSig(mode === 'map' ? null : about), seq };
    const t = titleOf(mode === 'map' ? null : about);
    const title = mode === 'map' ? (part === 'labels' ? 'Test labels' : part === 'elevs' ? 'Elevations' : 'Word before the elevation') : t.title;
    side.open({
      key: `insp:${seq}`,
      title,
      ...(mode !== 'map' && t.dot ? { dot: t.dot } : {}),
      ...(about && mode !== 'map' ? { about } : {}),
      ...(mode === 'part' && about ? { more: { label: 'All settings', onClick: () => openInspector(about, 'all') } } : {}),
      onClose: () => leaveInspector(seq),
      build: (body, nav) => buildInspector(body, nav),
      sync: () => {
        for (const f of inspSyncs) f();
      },
    });
    barRoomAt = null;
    positionContext();
    updateBubble();
    if (focus) requestAnimationFrame(() => focusField(focus));
  }
  /** A field of the inspector typed in: the caret in it (and so the keyboard, or the pad). */
  function focusField(f: Focus): void {
    if (f === 'words') return void sideText?.field?.focus();
    const el = f && side.el.querySelector<HTMLInputElement>(`input[data-k="${f}"]`);
    if (!el) return;
    el.focus({ preventScroll: true });
    el.select();
  }
  /** What it was about let go of: what is pending taken, what is left empty gone. */
  function leaveInspector(seq: number): void {
    const leaves = inspLeaves;
    inspLeaves = [];
    inspSyncs = [];
    sideText = null;
    for (const f of leaves) f();
    if (insp?.seq === seq) insp = null;
    barRoomAt = null;
    positionContext();
    updateBubble();
  }
  function buildInspector(body: HTMLElement, nav: SideNav): void {
    inspSyncs = [];
    inspLeaves = [];
    sideText = null;
    const s = insp;
    const ed = editor;
    if (!s || !ed) return;
    s.sig = inspectorSig(s.about);
    const live = (part: string) => (fn: () => void) => editor?.within(`insp:${s.seq}:${part}`, fn);
    if (s.mode === 'map') {
      if (s.part === 'labels') return labelsSection(body, live('labels'));
      if (s.part === 'elevs') return elevsSection(body, live('elevs'));
      return void append(body, wordSection(null, live('word'), nav, true));
    }
    const id = s.about;
    if (!id || !(isSharedLabel(id) ? ed.layout().shared.has(id) : ed.getItem(id))) return emptySection(body, nav);
    // one part, on its own: what the bar's button was for (done with, it goes back into its pill)
    if (s.mode === 'part' && s.part) return void append(body, partSection(id, s.part, live(s.part), nav, true));
    allSections(body, id, live, nav);
  }
  /**
   * A page over the one there (a retest, a depth, every word ready to use): built from the plan as it
   * now is, kept in step with it while on top, and whatever it does as it goes.
   */
  function subPage(title: string, fill: (body: HTMLElement, nav: SideNav) => void): SidePage {
    let syncs: (() => void)[] = [];
    let leaves: (() => void)[] = [];
    return {
      title,
      build: (body, nav) => {
        syncs = [];
        leaves = [];
        const was = [inspSyncs, inspLeaves] as const;
        inspSyncs = syncs;
        inspLeaves = leaves;
        try {
          fill(body, nav);
        } finally {
          [inspSyncs, inspLeaves] = was;
        }
      },
      sync: () => {
        for (const f of syncs) f();
      },
      leave: () => {
        for (const f of leaves.splice(0)) f();
      },
    };
  }
  /** Every one of some choices at once, on a page of their own: a tap takes one, and back. */
  function choicesPage(title: string, groups: Parameters<typeof sideChoices>[0]['groups'], now: () => string | null, onPick: (v: string) => void, typed = ''): SidePage {
    return subPage(title, (body, nav) => append(body, sideChoices({ groups, now: now(), typed, onPick: (v) => (onPick(v), nav.pop()) })));
  }
  /** Built again where it is (what it is about has changed): the page on top, no close, the same undo step. */
  function refillInspector(): void {
    const s = insp;
    if (!s) return;
    side.refill(s.mode === 'map' ? undefined : titleOf(s.about));
  }
  /**
   * The inspector kept with the plan: following the selection (all of it), or the same part to the
   * next thing that has it (else back into its pill); built again when what it holds has changed —
   * but never under a field being typed in (once that is done) — and its fields kept as they are.
   */
  function syncInspector(): void {
    updateBubble();
    const ed = editor;
    if (!ed || !insp || inspOpening) return;
    if (!side.key()) {
      insp = null;
      return;
    }
    if (insp.mode !== 'map') {
      const sel = ed.selection;
      const about = insp.about;
      const there = about && (isSharedLabel(about) ? ed.layout().shared.has(about) : !!ed.getItem(about));
      if (sel !== about || (about && !there)) {
        if (insp.mode === 'all') return openInspector(sel && (isSharedLabel(sel) || ed.getItem(sel)) ? sel : null, 'all');
        if (sel && insp.part && partsOf(sel).includes(insp.part)) return openInspector(sel, 'part', insp.part);
        return side.close();
      }
    }
    const sig = inspectorSig(insp.about);
    // (built again only when nothing in it is being typed in or pressed: a press begun on a button ends on that button)
    if (sig !== insp.sig && !side.el.contains(document.activeElement) && !inspPressed) {
      insp.sig = sig;
      return refillInspector();
    }
    side.sync();
  }
  // (a field typed in and done with, or a press let go: what waited for it is built now)
  let inspPressed = false;
  side.el.addEventListener('focusout', () => setTimeout(() => syncInspector(), 0), { signal: ac.signal });
  side.el.addEventListener('pointerdown', () => (inspPressed = true), { capture: true, signal: ac.signal });
  for (const t of ['pointerup', 'pointercancel'] as const)
    window.addEventListener(t, () => {
      if (!inspPressed) return;
      // (after the click the press makes, which may change it again)
      setTimeout(() => {
        inspPressed = false;
        syncInspector();
      }, 0);
    }, { capture: true, signal: ac.signal });
  /** The pill says what is selected: its colour, and its name to a screen reader. */
  function updateBubble(): void {
    const sel = editor?.selection ?? null;
    if (!sel) return side.setBubble({ label: 'Settings', dot: null });
    const t = titleOf(sel);
    side.setBubble({ label: `Settings: ${t.title}`, dot: t.dot ?? null });
  }
  /** A part of a page: its name small above it (none: it names itself), what it holds. */
  const section = (part: string, label: string | null, ...els: (HTMLElement | null | false)[]): HTMLElement =>
    h('div', { class: 'side-section', dataset: { part } }, label ? sideLabel(label) : null, ...els.filter((x): x is HTMLElement => !!x));
  /** One part of a thing, on its own (a bar's button) or among the rest. */
  function partSection(id: string, part: Part, live: (fn: () => void) => void, nav: SideNav, alone: boolean): HTMLElement[] {
    const it = isSharedLabel(id) ? undefined : editor?.getItem(id);
    switch (part) {
      case 'name':
        return it?.t === 'test' ? [section('name', null, sideRows(sideField('Test number', nameField(it))))] : [];
      case 'number':
        return it?.t === 'elev' ? [section('number', null, sideRows(sideField('Shot number', numberField(it))))] : [];
      case 'words': {
        const label = it?.t === 'test' || it?.t === 'elev' ? 'Note' : it?.t === 'callout' && it.template !== 'custom' ? 'Its box' : 'Words';
        return [section('words', label, ...wordsSection(id, live, nav))];
      }
      case 'retest':
        return it?.t === 'test' ? [section('retest', null, ...retestSection(it, live, () => nav.close(), alone))] : [];
      case 'depth':
        return it?.t === 'elev' ? [section('depth', null, ...depthSection(it, live, () => nav.close(), alone))] : [];
      case 'word':
        return it?.t === 'elev' ? [section('word', null, ...wordSection(id, live, nav, alone))] : [];
      default:
        return [];
    }
  }
  /** Everything that can be changed about a thing, in the order an inspector reaches for it. */
  function allSections(body: HTMLElement, id: string, live: (part: string) => (fn: () => void) => void, nav: SideNav): void {
    const ed = editor!;
    const P = (part: Part) => partSection(id, part, live(part), nav, false);
    if (isSharedLabel(id)) return void append(body, [...P('words'), sharedRows(id)]);
    const it = ed.getItem(id);
    switch (it?.t) {
      case 'test':
        append(body, [testCard(it, nav), retestRow(it, nav), ...P('words'), placeRows(it)]);
        break;
      case 'elev':
        append(body, [shotCard(it, nav), depthRow(it, nav), ...P('words'), placeRows(it)]);
        break;
      case 'callout':
        append(body, [...P('words'), ...calloutRows(it)]);
        break;
      case 'note':
        append(body, [...P('words'), noteColourRows(it)]);
        break;
      case 'area':
        append(body, areaRows(it));
        break;
      case 'hl':
        append(body, hlRows(it));
        break;
      case 'cut':
        append(body, shapeRows(it));
        break;
    }
  }
  /** Nothing selected: so it says — and the map's own settings, each a page on (and back). */
  function emptySection(body: HTMLElement, nav: SideNav): void {
    const ed = editor!;
    const live = (part: string) => (fn: () => void) => editor?.within(`insp:${insp?.seq ?? 0}:${part}`, fn);
    append(body, [
      h('p', { class: 'side-empty' }, 'Tap a test, a label or a mark on the plan, and what can be changed about it is here.'),
      ed.doc.kind === 'soils'
        ? section('map', 'This map', sideRows(
            sideRow({ icon: ICONS.tag, label: 'Test labels', detail: testName({ date: ed.doc.soils.date, initials: ed.doc.soils.initials, n: ed.nextNumber(ed.testMethod, numberFloor[ed.testMethod]) }, style()), chevron: true, onClick: () => nav.push(subPage('Test labels', (b) => labelsSection(b, live('labels')))) }),
            sideRow({ icon: ICONS.elev, label: 'Elevations', detail: `Next #${ed.nextElevNumber()}`, chevron: true, onClick: () => nav.push(subPage('Elevations', (b) => elevsSection(b, live('elevs')))) }),
          ))
        : null,
    ]);
  }
  /**
   * A test, at the head of its page: its number, how it was taken and whether it failed — on one card,
   * the three things said of every test.
   */
  function testCard(t: TestPoint, _nav: SideNav): HTMLElement {
    const ed = editor!;
    const method = segmented<TestMethod>(TEST_METHODS.map((m) => [m, m === 'sandcone' ? 'Sand cone' : 'Nuke']), methodOf(t), (v) => ed.setTestMethod(t.id, v, numberFloor[v]));
    method.setAttribute('aria-label', 'Taken by');
    return section(
      'name',
      null,
      sideRows(
        sideField('Test number', nameField(t)),
        sideField('Taken by', method),
        sideSwitch({ icon: ICONS.fail, label: 'Failing', danger: true, on: t.status === 'fail', onChange: (on) => ed.updateItem<TestPoint>(t.id, (x) => ({ ...x, status: on ? 'fail' : x.retestOf ? 'pass' : 'none' })) }),
      ),
    );
  }
  /** Whether a test retests another, and which: a row on to its page. */
  function retestRow(t: TestPoint, nav: SideNav): HTMLElement {
    const ed = editor!;
    const now = t.retestOf ? `${badgeRef(ownNameOf(ed.doc.items, t.retestOf, t), t.retestOf, style())}` : 'None';
    const live = (fn: () => void) => editor?.within(`insp:${insp?.seq ?? 0}:retest`, fn);
    return section('retest', 'Retest', sideRows(sideRow({ icon: ICONS.retest, label: 'Retest of', detail: now, chevron: true, onClick: () => nav.push(subPage('Retest', (b, n) => append(b, retestSection(ed.getItem(t.id) as TestPoint, live, () => n.pop(), false)))) })));
  }
  /** A shot, at the head of its page: its elevation (typed on the plan), its number, the word before it. */
  function shotCard(e: Elevation, nav: SideNav): HTMLElement {
    const ed = editor!;
    const value = sideRow({ icon: ICONS.edit, label: 'Elevation', detail: shownValue(e.value) || '—', chevron: true, aria: 'Type the elevation', onClick: () => ed.beginElevEdit(e.id) });
    const v = value.querySelector('.row-detail')!;
    inspSyncs.push(() => {
      const cur = editor?.getItem(e.id);
      if (cur?.t === 'elev') v.textContent = shownValue(cur.value) || '—';
    });
    const live = (fn: () => void) => editor?.within(`insp:${insp?.seq ?? 0}:word`, fn);
    return section(
      'value',
      null,
      sideRows(
        value,
        sideField('Shot number', numberField(e)),
        sideRow({ icon: ICONS.elev, label: 'Word before it', detail: prefixOf(e, ed.doc.elev) || 'None', chevron: true, onClick: () => nav.push(subPage('Word', (b, n) => append(b, wordSection(e.id, live, n, false)))) }),
      ),
    );
  }
  /** How far below a grade a shot is: a row on to its page. */
  function depthRow(e: Elevation, nav: SideNav): HTMLElement {
    const ed = editor!;
    const live = (fn: () => void) => editor?.within(`insp:${insp?.seq ?? 0}:depth`, fn);
    return section('depth', 'Depth', sideRows(sideRow({ icon: ICONS.ruler, label: 'Below a grade', detail: depthText(e.value, e.ref, ed.doc.elev) ?? 'None', chevron: true, onClick: () => nav.push(subPage('Depth', (b, n) => append(b, depthSection(ed.getItem(e.id) as Elevation, live, () => n.pop(), false)))) })));
  }
  /** A test's number (or its own name), set when typed (Return, the caret moving on, Done, the inspector moving on). */
  function nameField(t: TestPoint): HTMLInputElement {
    const id = t.id;
    const name = padField(h('input', { class: 'input mono', type: 'text', 'data-k': 'name', 'aria-label': 'Test number', value: shownNumber(t), placeholder: String(t.n), autocapitalize: 'characters', autocomplete: 'off', spellcheck: false, enterkeyhint: 'done' }), 'mixed');
    const commit = () => {
      const cur = editor?.getItem(id);
      if (cur?.t !== 'test') return;
      const v = name.value.trim();
      if (v === shownNumber(cur)) return;
      if (/^\d+$/.test(v) && (Number(v) < 1 || Number(v) > 9999)) {
        name.value = shownNumber(cur);
        return toast('Enter a number from 1 to 9999', 'error');
      }
      editor!.nameTest(id, v, numberFloor[methodOf(cur)]);
    };
    inspLeaves.push(commit);
    name.addEventListener('change', commit);
    name.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') (e.preventDefault(), commit(), name.blur());
    });
    inspSyncs.push(() => {
      const cur = editor?.getItem(id);
      if (cur?.t === 'test' && document.activeElement !== name) name.value = shownNumber(cur);
    });
    return name;
  }
  /** A shot's number, set when typed. */
  function numberField(e: Elevation): HTMLInputElement {
    const id = e.id;
    const num = padField(h('input', { class: 'input mono', type: 'text', inputmode: 'numeric', 'data-k': 'number', 'aria-label': 'Shot number', value: String(e.n), autocomplete: 'off', enterkeyhint: 'done' }), 'num', { point: false });
    const commit = () => {
      const cur = editor?.getItem(id);
      if (cur?.t !== 'elev') return;
      const v = Math.floor(Number(num.value.trim()));
      if (v === cur.n) return;
      if (!Number.isFinite(v) || v < 1 || v > 9999) {
        num.value = String(cur.n);
        return toast('Enter a number from 1 to 9999', 'error');
      }
      editor!.renumberElev(id, v);
    };
    inspLeaves.push(commit);
    num.addEventListener('change', commit);
    num.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') (ev.preventDefault(), commit(), num.blur());
    });
    inspSyncs.push(() => {
      const cur = editor?.getItem(id);
      if (cur?.t === 'elev' && document.activeElement !== num) num.value = String(cur.n);
    });
    return num;
  }
  /**
   * A label's words (2026-10-28, the inspectors: everything about a label's words, a tap away, the plan
   * still in hand): its own words in their own looks (bold, italic, underlined, struck, a colour) on a
   * card of paper, as on the plan, their looks along its top — and, for a markup's label, a row at its
   * foot on to every word ready to use, all at once — then its box's size and where its lines sit (its
   * width is its grips', on the plan). What is typed shows on the plan as it is typed.
   */
  function wordsSection(id: string, live: (fn: () => void) => void, nav: SideNav): HTMLElement[] {
    const ed = editor!;
    const shared = isSharedLabel(id);
    const it = shared ? undefined : ed.getItem(id);
    const d = ed.doc;
    const n = shared && id !== FAIL_LABEL ? (markupOfLabel(id) ?? 1) : 0;
    const parts: (HTMLElement | null)[] = [];
    // its own words: a station callout's are typed on it, in place — here only its box's looks; one
    // being typed on the plan is written there
    const stations = it?.t === 'callout' && it.template !== 'custom';
    const typedThere = it?.t === 'callout' && editingId === id;
    const words = !stations && !typedThere;
    const auto = shared ? (id === FAIL_LABEL ? failLabelText(d, 1) : areaLabelText(d, 1, n)) : '';
    const placeholder = shared ? auto : it?.t === 'test' ? 'e.g. NE corner of pad' : it?.t === 'elev' ? 'e.g. bottom of removal' : 'Words';
    // (its words, their looks and its box's: one undo step, as the part's other changes are)
    const key = `insp:${insp?.seq ?? 0}:words`;
    void live;
    const field = words
      ? richField({
          runs: ed.labelRuns(id),
          placeholder,
          label: shared ? `${titleOf(id).title}` : it?.t === 'test' || it?.t === 'elev' ? 'Note' : 'Words',
          multiline: true,
          onInput: (runs) => {
            editor?.setLabelRuns(id, runs, key);
            looks.sync();
          },
          onCaret: () => looks.sync(),
        })
      : null;
    // (its own colour: what its words are drawn in, unless given another)
    const own = id === FAIL_LABEL ? darkOf(FAIL) : it?.t === 'elev' ? elevInk(elevColor(d.elev?.color)) : it?.t === 'callout' ? darkOf(calloutColor(d, it)) : INK;
    const looks = lookRows(
      field,
      {
        size: () => editor?.labelLook(id)?.size ?? 1,
        setSize: (k) => editor?.setLabelLook(id, { size: k }, key),
        align: () => editor?.labelLook(id)?.align ?? 'left',
        setAlign: (a) => editor?.setLabelLook(id, { align: a === 'left' ? undefined : a }, key),
        fitted: () => editor?.labelLook(id)?.w !== undefined,
        fit: () => editor?.setLabelLook(id, { w: undefined }, key),
      },
      (anchor, now, pick) => openTextColours(anchor, now, own, pick),
      own,
    );
    // a markup's label: every word ready to use, a row at the foot of its card on to all of them at once
    let ready: HTMLElement | null = null;
    if (shared && field) {
      const recent = id === FAIL_LABEL ? [] : app.state.settings.labelPhrases.filter((p) => !MARKUP_PHRASES.includes(p));
      const usual = id === FAIL_LABEL ? FAIL_PHRASES : MARKUP_PHRASES;
      const pick = (t: string) => {
        const runs: TextRun[] = [{ t }];
        editor?.setLabelRuns(id, runs, key);
        if (n) rememberPhrase(t);
      };
      ready = sideRow({ icon: ICONS.ready, label: 'Words ready to use', detail: String(recent.length + usual.length), chevron: true, onClick: () => nav.push(choicesPage('Ready to use', [{ label: 'Used lately', items: recent.map((v) => ({ value: v })) }, { label: recent.length ? 'Usual' : undefined, items: usual.map((v) => ({ value: v })) }], () => plainOf(editor?.labelRuns(id) ?? []), pick)) });
      ready.classList.add('ready');
    }
    // the words on a card of paper (as on the plan), their looks along its top, the way to the words ready to use at its foot; the box's under it
    if (field) parts.push(h('div', { class: 'rich-card' }, looks.toolbar, field.el, ready));
    if (stations) parts.push(sideRows(sideRow({ icon: ICONS.edit, label: 'Type its stations', chevron: true, onClick: () => beginCalloutEdit(id, null) })));
    if (typedThere) parts.push(sideHelp('Being typed on the plan.'));
    if (looks.box) parts.push(looks.box);
    sideText = { id, field };
    inspSyncs.push(() => {
      const cur = editor;
      if (!cur) return;
      // undone (or redone) under it: what it shows follows — but never what is being typed, as typed
      if (field) {
        const norm = (runs: readonly TextRun[]) => JSON.stringify(tidyRuns(runs, 1000) ?? [{ t: plainOf(runs).trim() }]);
        const now = cur.labelRuns(id);
        if (norm(now) !== norm(field.runs())) field.set(now);
      }
      looks.sync();
    });
    inspLeaves.push(() => {
      if (n && field) rememberPhrase(plainOf(field.runs()));
      // (a note left with no words is no note)
      const cur = editor?.getItem(id);
      if (cur?.t === 'note' && !cur.text.trim()) editor!.commit({ ...editor!.doc, items: editor!.doc.items.filter((x) => x.id !== id) });
    });
    return parts.filter((x): x is HTMLElement => !!x);
  }
  /** Where a test's label or a shot's tag stands: back to where the map would put it, once put by hand. */
  function placeRows(it: TestPoint | Elevation): HTMLElement | null {
    if (!it.label) return null;
    const ed = editor!;
    return section('place', it.t === 'test' ? 'Its label' : 'Its tag', sideRows(sideRow({ icon: ICONS.reset, label: 'Place it automatically', onClick: () => ed.updateItem<TestPoint | Elevation>(it.id, (x) => ({ ...x, label: null })) })));
  }
  /** A note's colour. */
  function noteColourRows(n: Note): HTMLElement {
    return section(
      'colour',
      'Colour',
      h('div', { class: 'chips wrap', role: 'group', 'aria-label': 'Note colour' }, ...Object.entries(NOTE_COLORS).map(([key, color]) => h('button', { class: 'chip', type: 'button', 'aria-pressed': String(n.color === key), onClick: () => (editor?.updateItem<Note>(n.id, (x) => ({ ...x, color: key })), haptic()) }, h('span', { class: 'swatch', style: { background: color } }), key[0]!.toUpperCase() + key.slice(1)))),
    );
  }
  /** Where a markup's label stands, and its arrows: back to where the map would put them, once put by hand. */
  function sharedRows(id: string): HTMLElement | null {
    const ed = editor!;
    const rows = [
      ed.sharedLabelPinned(id) ? sideRow({ icon: ICONS.reset, label: 'Place it automatically', onClick: () => ed.autoPlaceShared(id) }) : null,
      ed.sharedArrowsPinned(id) ? sideRow({ icon: ICONS.arrows, label: ed.sharedArrowsOff(id) ? 'Arrows back' : 'Arrows to the middle', onClick: () => ed.resetSharedArrows(id) }) : null,
    ].filter((x): x is HTMLButtonElement => !!x);
    return rows.length ? section('place', 'Where it stands', sideRows(...rows)) : null;
  }
  /** A callout's arrows, where it stands, and its kind of words. */
  function calloutRows(c: Callout): HTMLElement[] {
    const ed = editor!;
    const id = c.id;
    const soils = ed.doc.kind === 'soils';
    const text = c.template === 'custom';
    // (a task on the plan: the inspector goes back into its pill, out of its way)
    const task = (fn: () => void) => () => (side.close(), fn());
    return [
      section('arrows', 'Arrows', sideRows(
        sideRow({ icon: ICONS.arrows, label: 'Choose what it points at', chevron: true, onClick: task(() => ed.setPointing(true)) }),
        ed.hasPinnedArrows(id) ? sideRow({ icon: ICONS.reset, label: ed.hasArrowOff(id) ? 'Arrows back' : 'Arrows to the middle', onClick: () => ed.centreArrows(id) }) : null,
      )),
      c.box ? section('place', 'Where it stands', sideRows(sideRow({ icon: ICONS.reset, label: 'Position automatically', onClick: () => ed.updateItem<Callout>(id, (cur) => ({ ...cur, box: null })) }))) : null,
      section('kind', 'Its words', sideRows(
        soils ? null : text
          ? sideRow({ icon: ICONS.callout, label: 'Use station fields', onClick: () => (ed.editCallout(id, { template: 'between' }, 'template'), beginCalloutEdit(id, null)) })
          : sideRow({ icon: ICONS.text, label: 'Write custom text', onClick: () => {
              const cur = ed.getItem(id);
              if (cur?.t !== 'callout') return;
              ed.editCallout(id, { template: 'custom', custom: cur.custom.trim() ? cur.custom : calloutLines(cur).join('\n') }, 'template');
            } }),
        !soils && !c.auto && ed.canFillFromPlan(id) ? sideRow({ icon: ICONS.callout, label: 'Stations from the plan', onClick: () => ed.fillFromPlan(id) }) : null,
        sideRow({ icon: ICONS.undo, label: 'Clear and start over', danger: true, onClick: () => {
          ed.updateItem<Callout>(id, (cur) => {
            const { materialName: _was, ranges: _ranges, auto: _auto, ...rest } = cur;
            const swatch = cur.material ? materialByKey(cur.material).swatchOnly : false;
            return { ...rest, template: soils ? 'custom' : 'between', from: '', to: '', custom: '', ...(swatch ? { materialName: '' } : {}) };
          });
        } }),
      )),
    ].filter((x): x is HTMLElement => !!x);
  }
  /** Which markup (or pour) a thing is in, chosen again: the numbers in their colours, its own lit, and a new one. */
  function moverChips(o: { word: string; count: number; own: number; canNew: boolean; colour?: (n: number) => { fill: string; stroke: string }; move: (n: number) => void }): HTMLElement {
    const chips = h('div', { class: 'chips wrap', role: 'group', 'aria-label': o.word });
    for (let n = 1; n <= o.count; n++) {
      const c = o.colour?.(n);
      chips.append(h('button', { class: 'chip', type: 'button', 'aria-pressed': String(n === o.own), 'aria-label': `${o.word} ${n}`, onClick: () => (n !== o.own && o.move(n), haptic()) }, c ? h('span', { class: 'swatch', style: { background: c.fill, boxShadow: `inset 0 0 0 2px ${c.stroke}` } }) : null, String(n)));
    }
    if (o.canNew) chips.append(h('button', { class: 'chip', type: 'button', 'aria-label': `A new ${o.word.toLowerCase()}`, onClick: () => (o.move(o.count + 1), haptic()) }, icon(ICONS.plus, 16), 'New'));
    return chips;
  }
  /** A work area: its markup (or failing), its label's words, a callout of its own, its shape. */
  function areaRows(a: WorkArea): HTMLElement[] {
    const ed = editor!;
    const fail = a.status === 'fail';
    const n = markupOf(a);
    const labelId = fail ? FAIL_LABEL : markupLabelId(n);
    const alone = !ed.doc.items.some((x) => x.t === 'area' && x.id !== a.id && x.status !== 'fail' && markupOf(x) === n);
    return [
      fail ? null : section('markup', 'Markup', moverChips({ word: 'Markup', count: ed.markupCount, own: n, canNew: !(alone && n === ed.markupCount), colour: (m) => areaColors(ed.markupColour(m)), move: (m) => ed.moveMarkup(a.id, m) })),
      section('failing', null, sideRows(sideSwitch({ icon: ICONS.fail, label: 'Failing area', danger: true, on: fail, onChange: (on) => ed.setAreaFailing(a.id, on) }))),
      section('label', 'Its label', sideRows(
        sideRow({ icon: ICONS.text, label: plainOf(ed.labelRuns(labelId)).replace(/\s+/g, ' ') || 'Its words', chevron: true, aria: 'Label text', onClick: () => openInspector(labelId, 'part', 'words') }),
        sideRow({ icon: ICONS.callout, label: 'Add a callout', onClick: () => ed.addCallout(null, centerOf(a) ?? { x: ed.doc.w / 2, y: ed.doc.h / 2 }, a.id) }),
      )),
      ...shapeRows(a),
    ].filter((x): x is HTMLElement => !!x);
  }
  /** A concrete mark: its pour, a callout of its own, its shape. */
  function hlRows(m: Highlight): HTMLElement[] {
    const ed = editor!;
    const own = placementOf(m);
    const alone = !ed.doc.items.some((x) => x.t === 'hl' && x.id !== m.id && placementOf(x) === own);
    return [
      section('pour', 'Pour', moverChips({ word: 'Pour', count: ed.placementCount, own, canNew: !(alone && own === ed.placementCount), move: (n) => ed.movePlacement(m.id, n) })),
      section('callout', null, sideRows(sideRow({ icon: ICONS.callout, label: 'Add a callout', onClick: () => ed.addCallout(m.material, centerOf(m) ?? { x: ed.doc.w / 2, y: ed.doc.h / 2 }, m.id) }))),
      ...shapeRows(m),
    ];
  }
  /** A shape's points, what is cut out of it, whether its points snap to the plan, and a band's width. */
  function shapeRows(it: WorkArea | Highlight | Item): HTMLElement[] {
    const ed = editor!;
    const shaped = it.t === 'area' || it.t === 'hl' ? it : null;
    const band = shaped?.shape.k === 'stroke';
    // (a task on the plan: the inspector goes back into its pill, out of its way)
    const task = (fn: () => void) => () => (side.close(), fn());
    const width = (k: number) => () =>
      ed.updateItem(it.id, (y) => ((y.t === 'area' || y.t === 'hl') && y.shape.k === 'stroke' ? { ...y, shape: { ...y.shape, width: k > 1 ? y.shape.width * k : Math.max(2, y.shape.width * k) } } : y));
    return [
      section('shape', 'Shape', sideRows(
        band ? sideRow({ icon: ICONS.edit, label: 'Edit points', chevron: true, onClick: task(() => ed.setEditing(true)) }) : ed.canEditPoints ? sideRow({ icon: ICONS.edit, label: 'Edit points', chevron: true, onClick: task(() => ed.editPoints(true)) }) : null,
        shaped ? sideRow({ icon: ICONS.cut, label: 'Cut out', chevron: true, onClick: task(() => ed.setCutting(true)) }) : null,
        sideSwitch({ icon: ICONS.magnet, label: 'Snap to the plan', on: ed.snapAnchors, onChange: (on) => {
          ed.snapAnchors = on;
          void updateSettings({ snapAnchors: on });
          renderContext();
        } }),
      )),
      band ? section('width', 'Width', sideRows(sideRow({ icon: ICONS.thinner, label: 'Thinner', onClick: width(1 / 1.2) }), sideRow({ icon: ICONS.wider, label: 'Wider', onClick: width(1.2) }))) : null,
    ].filter((x): x is HTMLElement => !!x);
  }
  /** A field's value taken when it is done with: Return, or the caret moving on (the panel's Done too: the caret leaves it). */
  function onCommit(input: HTMLInputElement, fn: () => void): void {
    input.addEventListener('change', fn);
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') (e.preventDefault(), input.blur());
    });
  }
  /** A label's words in the inspector (its name, its number): opened on that part, the field typed in at once (`focus`; none: the keyboard waits). */
  function openTextPanel(id: string, focus: 'words' | 'name' | 'number' | null = 'words'): void {
    openInspector(id, 'part', focus === 'name' ? 'name' : focus === 'number' ? 'number' : 'words', focus);
  }
  /** The colours a stretch of words may take: the label's own first, the palette's, and one's own. */
  function openTextColours(anchor: HTMLElement, now: string | null, own: string, pick: (c: string | null) => void): void {
    openPopover(anchor, (close) => {
      const sw = (c: string | null, name: string, colour: string) =>
        h('button', { class: 'sw', type: 'button', 'aria-label': name, title: name, 'aria-pressed': String((now ?? null) === c), style: { '--sw-fill': colour, '--sw-stroke': colour }, onClick: () => (pick(c), haptic(), close()) });
      const well = h('input', { type: 'color', value: now?.startsWith('#') ? now : '#1a1a1a', 'aria-label': 'A colour of your own' });
      well.addEventListener('change', () => (pick(well.value.toLowerCase()), close()));
      const wellBtn = h('label', { class: 'sw well', title: 'Your own', 'aria-pressed': String(!!now?.startsWith('#')), ...(now?.startsWith('#') ? { style: { '--sw-fill': now, '--sw-stroke': now } } : {}) }, well);
      return [
        h('div', { class: 'palette', role: 'group', 'aria-label': 'Colour of the words' }, sw(null, 'Its own colour', own), ...TEXT_COLORS.filter((c) => c.color !== own).map((c) => sw(c.key, c.name, c.color)), wellBtn),
        h('div', { class: 'palette-caption' }, now ? (TEXT_COLORS.find((c) => c.key === now)?.name ?? 'Your own') : 'Its own colour'),
      ];
    }, { above: false, label: 'Colour of the words' });
    void textColor;
  }
  /** A markup's label's words: in the side panel. */
  function editMarkupLabel(n: number): void {
    openTextPanel(markupLabelId(n), null);
  }
  function editFailLabel(): void {
    openTextPanel(FAIL_LABEL, null);
  }
  /** A label's words used: first among the ones offered next time (eight kept, on this device). */
  function rememberPhrase(text: string): void {
    const t = text.trim();
    if (!t || MARKUP_PHRASES.includes(t)) return;
    const list = [t, ...app.state.settings.labelPhrases.filter((x) => x !== t)].slice(0, 8);
    void updateSettings({ labelPhrases: list });
  }

  /* ------------------------------------------------------- elevations */

  /**
   * What a tag calls an elevation, from a short list (the map's own word, or — `id` — one shot's): the
   * plain "Elev." first, then the grades inspectors shoot, none at all, or a word of one's own.
   */
  function chooseElevWord(anchor: HTMLElement, id: string | null): void {
    const ed = editor;
    if (!ed) return;
    const set = (w: string | null) => {
      haptic();
      if (id) ed.setElevPrefix(id, w === mapPrefix(ed.doc.elev) ? null : w);
      else ed.setElevMeta({ prefix: w === ELEV_PREFIX_DEFAULT ? undefined : (w ?? undefined) });
      renderInfobar();
    };
    const shot = id ? ed.doc.items.find((x): x is Elevation => x.t === 'elev' && x.id === id) : undefined;
    const now = shot ? prefixOf(shot, ed.doc.elev) : mapPrefix(ed.doc.elev);
    // the word, short as the tag has it, and what it stands for
    const items: MenuItem[] = ELEV_WORDS.map(([w, what]) => ({ label: w || 'No word', detail: w ? what : '', checked: now === w, onSelect: () => set(w) }));
    const own = !!now && !ELEV_WORDS.some(([w]) => w === now);
    if (own) items.push({ label: now, detail: 'your own', checked: true, onSelect: () => set(now) });
    openMenu(anchor, [
      ...items,
      MENU_SEP,
      { label: 'A word of my own…', icon: ICONS.edit, onSelect: () => openInspector(id, id ? 'part' : 'map', 'word') },
    ], { above: true });
  }
  /**
   * The word before a shot's elevation (its own: `id`; null: the map's): every usual one at once, the
   * one it is ticked, or a few letters of one's own, each letter on the tags as it is typed.
   */
  function wordSection(id: string | null, live: (fn: () => void) => void, nav: SideNav, alone: boolean): HTMLElement[] {
    const ed = editor!;
    const now = (): string => {
      const shot = id ? ed.doc.items.find((x): x is Elevation => x.t === 'elev' && x.id === id) : undefined;
      return shot ? prefixOf(shot, ed.doc.elev) : mapPrefix(ed.doc.elev);
    };
    const set = (w: string | null) => {
      if (id) ed.setElevPrefix(id, w === mapPrefix(ed.doc.elev) ? null : w);
      else ed.setElevMeta({ prefix: w === ELEV_PREFIX_DEFAULT ? undefined : (w ?? undefined) });
      renderInfobar();
    };
    const usual = (w: string) => ELEV_WORDS.some(([x]) => x === w);
    const input = h('input', { class: 'input', type: 'text', value: usual(now()) ? '' : now(), placeholder: 'e.g. BOR', maxlength: 12, autocapitalize: 'characters', autocomplete: 'off', 'aria-label': 'A word of your own', enterkeyhint: 'done' });
    // (each letter shows on the tags as it is typed)
    input.addEventListener('input', () => live(() => set(input.value.trim().slice(0, 12))));
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') (e.preventDefault(), alone ? nav.close() : input.blur());
    });
    // (a usual word taken: back, the word on the shot's row)
    const pick = (w: string) => {
      live(() => set(w));
      if (!alone) nav.pop();
      else side.refill();
    };
    return [
      ...sideChoices({ groups: [{ items: ELEV_WORDS.map(([w, what]) => ({ value: w, text: w || 'No word', detail: what || undefined })) }], now: now(), onPick: pick }),
      section('own', 'A word of your own', sideRows(sideField('Word', input)), sideHelp('A few letters: what the shots are of.')),
    ];
  }
  function editElevSettings(): void {
    openInspector(null, 'map', 'elevs');
  }
  /**
   * The map's elevations: where their numbering starts, their colour and unit, and — said once in the
   * legend — what was shot and how, each with every usual answer a page on. Nothing here is ever read
   * off the plan.
   */
  function elevsSection(body: HTMLElement, live: (fn: () => void) => void): void {
    const ed = editor!;
    const meta = ed.doc.elev ?? {};
    const next = ed.nextElevNumber();
    const num = padField(h('input', { class: 'input mono', type: 'text', inputmode: 'numeric', value: String(next), 'aria-label': 'Next shot number', enterkeyhint: 'done' }), 'num', { point: false });
    onCommit(num, () => {
      const n = Math.floor(Number(num.value));
      const was = ed.nextElevNumber();
      if (n === was) return;
      if (!Number.isFinite(n) || n < 1 || n > 9999) {
        num.value = String(was);
        return toast('Enter a number from 1 to 9999', 'error');
      }
      live(() => ed.setElevMeta({ start: n }));
      renderInfobar();
    });
    let colour = meta.color ?? ELEV_COLORS[0]!.key;
    // this map's tag colour; and, a tap more, the one new maps start with (Settings has it too)
    const asDefault = h('button', { class: 'chip', type: 'button', onClick: () => {
      void updateSettings({ elevDefaults: { ...app.state.settings.elevDefaults, color: colour } });
      toast(`New maps' elevation tags will be ${elevColorName(colour).toLowerCase()}`, 'success');
      paintDefault();
    } }, 'Make it the default for new maps');
    const paintDefault = () => {
      asDefault.hidden = colour === (app.state.settings.elevDefaults.color ?? ELEV_COLORS[0]!.key);
    };
    const colours = elevColourPicker(colour, (c) => {
      colour = c;
      paintDefault();
      live(() => ed.setElevMeta({ color: c === ELEV_COLORS[0]!.key ? undefined : c }));
      renderInfobar();
    });
    paintDefault();
    const units = segmented<'ft' | 'm'>([['ft', 'Feet'], ['m', 'Metres']], meta.unit === 'm' ? 'm' : 'ft', (v) => live(() => ed.setElevMeta({ unit: v === 'm' ? 'm' : undefined })));
    /** What was shot (or how): typed, or one of the usual answers, all at once a page on. */
    const words = (key: 'what' | 'source', label: string, placeholder: string, list: string[]) => {
      const input = h('input', { class: 'input', type: 'text', value: meta[key] ?? '', placeholder, maxlength: 60, autocapitalize: 'off', 'aria-label': label, enterkeyhint: 'done' });
      const apply = () => {
        const v = input.value.trim();
        if (v !== (ed.doc.elev?.[key] ?? '')) live(() => ed.setElevMeta({ [key]: v || undefined }));
      };
      onCommit(input, apply);
      const ready = sideRow({ icon: ICONS.ready, label: 'Ready to use', detail: String(list.length), chevron: true, onClick: () => side.push(choicesPage(label, [{ items: list.map((v) => ({ value: v })) }], () => ed.doc.elev?.[key] ?? null, (v) => ((input.value = v), apply()))) });
      return section(key, label, h('div', { class: 'side-rows' }, input, ready));
    };
    append(body, [
      section('next', null, sideRows(sideField('Next shot number', num)), sideHelp('Shots are numbered on their own, apart from the tests. A deleted shot frees its number.')),
      section('colour', 'Tag colour on this map', colours, h('div', { class: 'chips wrap' }, asDefault)),
      section('unit', 'Unit', units),
      words('what', 'What was shot', 'e.g. bottom of over-excavation', ELEV_WHAT),
      words('source', 'How they were taken', 'e.g. GPS rover, contractor', ELEV_SOURCES),
      sideHelp('Said once in the legend. New maps start with the colour, word and unit in Settings.'),
    ]);
  }
  function editElevDepth(e: Elevation): void {
    openInspector(e.id, 'part', 'depth');
  }
  /**
   * The grade a shot is measured against, typed (never read off the plan): its name — or one of the
   * usual grades, every one at once — and its elevation; the tag then says how far below it the shot is.
   */
  function depthSection(e: Elevation, live: (fn: () => void) => void, done: () => void, alone: boolean): HTMLElement[] {
    const ed = editor!;
    // the grade last typed on this map is offered, never put in by itself
    const last = [...ed.doc.items].reverse().find((x): x is Elevation => x.t === 'elev' && x.id !== e.id && !!x.ref)?.ref;
    const name = h('input', { class: 'input mono', type: 'text', value: e.ref?.name ?? last?.name ?? 'FG', maxlength: 12, autocapitalize: 'characters', 'aria-label': 'Grade', enterkeyhint: 'done' });
    const value = padField(h('input', { class: 'input mono', type: 'text', inputmode: 'decimal', value: e.ref?.value ?? '', placeholder: last?.value ?? '2805.54', 'aria-label': 'Its elevation', enterkeyhint: 'done' }), 'num', { key: '±' });
    // set once the grade's elevation is typed: the tag says how far below it the shot is, there and then
    const apply = () => {
      const v = cleanElevation(value.value);
      if (!v) return;
      if (!isElevation(v)) return toast('Type the grade’s elevation', 'error');
      const ref = { name: name.value.trim().toUpperCase(), value: v };
      const now = (ed.getItem(e.id) as Elevation | undefined)?.ref;
      if (now?.name !== ref.name || now?.value !== ref.value) live(() => ed.setElevRef(e.id, ref));
    };
    onCommit(name, apply);
    onCommit(value, apply);
    // (a grade taken: in the field, ticked here, and set once there is an elevation to measure from)
    const grades = sideChoices({ groups: [{ label: 'Grades', items: ELEV_GRADES.map(([g, what]) => ({ value: g, detail: what })) }], now: name.value, onPick: (g) => {
      name.value = g;
      for (const b of grades[0]?.querySelectorAll('.side-row.option') ?? []) b.setAttribute('aria-pressed', String(b.getAttribute('aria-label') === g));
      apply();
    } });
    name.addEventListener('input', () => {
      const g = name.value.trim().toUpperCase();
      for (const b of grades[0]?.querySelectorAll('.side-row.option') ?? []) b.setAttribute('aria-pressed', String(b.getAttribute('aria-label') === g));
    });
    const out: HTMLElement[] = [
      section('grade', null, sideRows(sideField('Grade', name), sideField('Its elevation', value)), sideHelp(`The shot reads ${shownValue(e.value) || '—'}; the tag says how far below the grade it is — “9.89 ft below FG” — exactly to the places typed.`)),
      ...grades,
    ];
    if (e.ref) out.push(sideRows(sideRow({ icon: ICONS.close, label: 'No depth', danger: true, onClick: () => (live(() => ed.setElevRef(e.id, null)), done()) })));
    if (alone) requestAnimationFrame(() => value.focus({ preventScroll: true }));
    return out;
  }
  /*
   * A shot's elevation is typed on the number pad (numpad.ts), and shows as it is typed on the tag
   * itself: the tag lifts, its number and word as they will print, the digits after them with a
   * caret, the plan laying the tag out from what it shows. Done — or a tap anywhere else, which does
   * only that (the next tap places the next shot) — finishes. The pad opens where it was last put,
   * clear of the tag, else on the side away from it; carried by its grip, it is put back there next
   * time. Nothing is focused, so the system keyboard never comes up, and the plan never moves for the
   * pad; a keyboard attached to the iPad types too (digits, point, minus, Delete, Return).
   */
  let elevField: { id: string; el: HTMLElement; n: HTMLElement; word: HTMLElement; value: HTMLElement; depth: HTMLElement; text: string; placeholder: string } | null = null;
  // a shot's elevation is typed on the number pad, with a display of its own: whose, and what is typed
  const pad = createNumpad({ label: 'Elevation', withDisplay: true, key: (k) => typeOnShot(k), enter: () => finishElevTyping(), moved: (s) => rememberPad(s) });
  pad.el.classList.add('elev-pad');
  pad.setLayout({ extra: 'sign', point: true, enter: 'done', keyboard: false });
  untrackGlass.push(trackGlass(pad.el));
  screen.append(pad.el);
  /** The controls over the plan, which a pad over it keeps off (the top's, the tools and the strip, Fit): each as it stands, never the strip of screen it is in. */
  const chromeRects = (): PadRect[] =>
    [...[backBtn, title, ...topChrome.querySelectorAll<HTMLElement>('.cluster.trail > *'), toolbar, subtools.el, infobar.el, zoomer].filter((el) => !el.hidden && el.offsetParent !== null).map((el) => el.getBoundingClientRect()), ...(side.rect() ? [side.rect()!] : [])]
      .filter((r) => r.width > 0)
      .map((r) => ({ x: r.left, y: r.top, w: r.width, h: r.height }));
  // a field's pad over the plan keeps off the controls, and off what a callout being typed calls out
  padKeepOut(() => {
    const out = chromeRects();
    const ed = editor;
    const c = editingId && ed ? ed.getItem(editingId) : undefined;
    if (ed && c?.t === 'callout') {
      const sr = stage.getBoundingClientRect();
      for (const id of markIdsOf(c)) {
        const r = ed.anchorRect(id);
        if (r) out.push({ x: sr.left + r.x, y: sr.top + r.y, w: r.w, h: r.h });
      }
    }
    return out;
  });
  /**
   * Where the shot's pad stands: where it was last put, if that covers neither the tag nor a control;
   * else against the side away from the tag, level with it. The plan never moves for it. Once open it
   * stays put — moved only if the tag comes under it (`onlyIfCovering`).
   */
  function placeShotPad(onlyIfCovering = false): void {
    const ed = editor;
    const f = elevField;
    if (!ed || !f) return;
    const r = ed.anchorRect(f.id, true);
    if (!r) return;
    const sr = stage.getBoundingClientRect();
    const tag: PadRect = { x: sr.left + r.x, y: sr.top + r.y, w: r.w, h: r.h };
    const b = padBounds();
    pad.compact(false);
    let s = pad.size();
    if (b.h < s.h + 140) {
      pad.compact(true);
      s = pad.size();
    }
    if (onlyIfCovering && pad.at && !(pad.at.x < tag.x + tag.w + 12 && pad.at.x + s.w > tag.x - 12 && pad.at.y < tag.y + tag.h + 12 && pad.at.y + s.h > tag.y - 12)) return;
    const at = spotFor(s, [tag, ...chromeRects()], b, keptPad(b), tag);
    if (pad.shown) pad.moveTo(at, true);
    else pad.show(at, at.x > tag.x ? 'right' : 'left');
  }
  function elevPlaceholder(id: string): string {
    // the last elevation typed on the map: the same few digits, as a shape to type over (never put in)
    const last = [...(editor?.doc.items ?? [])].reverse().find((x): x is Elevation => x.t === 'elev' && x.id !== id && x.value !== '');
    return last ? last.value : '0000.00';
  }
  /** The tag being typed follows the editor: there while a shot is typed into, on that shot; gone, with the pad, when typing is over. */
  function syncElevField(): void {
    const ed = editor;
    const id = ed?.elevEditing ?? null;
    const it = id ? ed!.getItem(id) : undefined;
    if (!ed || !id || it?.t !== 'elev') {
      if (elevField) {
        elevField.el.remove();
        elevField = null;
      }
      pad.hide();
      screen.classList.remove('typing-shot');
      return;
    }
    // (on a small screen the pad stands where the strip is: the strip steps aside while a shot is typed)
    screen.classList.add('typing-shot');
    if (!elevField) {
      const n = h('span', { class: 'n' });
      const word = h('span', { class: 'w' });
      const value = h('span', { class: 'v' });
      const depth = h('span', { class: 'd' });
      const el = h('div', { class: 'elev-inplace', 'aria-hidden': 'true' }, n, word, value, depth);
      elevField = { id: '', el, n, word, value, depth, text: '', placeholder: '' };
      stage.parentElement!.insertBefore(el, pad.el);
    }
    const f = elevField;
    const word = prefixOf(it, ed.doc.elev);
    if (f.id !== id) {
      // a new shot (or another one typed into again): the pad stays where the thumb is
      f.id = id;
      f.text = it.value;
      f.placeholder = elevPlaceholder(id);
      f.el.style.setProperty('--tag', elevColor(ed.doc.elev?.color));
      f.el.style.setProperty('--tag-ink', elevInk(elevColor(ed.doc.elev?.color)));
      pad.display({ label: [`#${it.n}`, word], value: f.text, placeholder: f.placeholder });
      f.n.textContent = `#${it.n}`;
      f.word.textContent = word;
      fitElevField();
      // (the plan stays where it is: the pad goes where the tag is not)
      placeShotPad();
      return;
    }
    f.n.textContent = `#${it.n}`;
    f.word.textContent = word;
    fitElevField();
  }
  /** A key of the pad (or of a keyboard): the elevation as typed, kept plain — a sign, digits and one point. */
  function typeOnShot(k: PadKey): void {
    const f = elevField;
    const ed = editor;
    if (!f || !ed || k === 'plus' || k === 'minus') return;
    let v = f.text;
    if (k === 'back') v = v.slice(0, -1);
    else if (k === 'clear') v = '';
    else if (k === 'sign') v = v.startsWith('-') ? v.slice(1) : `-${v}`;
    else if (k === '.') {
      if (!v.includes('.')) v = (v === '' || v === '-' ? `${v}0` : v) + '.';
    } else v += k;
    v = cleanElevation(v);
    if (v === f.text) return;
    f.text = v;
    ed.setElevValue(f.id, v);
    pad.value(v);
    fitElevField();
  }
  /** The tag sized to what it shows, and laid over the tag on the plan, exactly; kept clear of the pad. */
  let refitTimer = 0;
  function fitElevField(): void {
    const f = elevField;
    const ed = editor;
    if (!f || !ed) return;
    // (what the tag shows of the value: the digits as typed, a minus as one; the placeholder when there are none)
    const shown = f.text ? f.text.replace(/^-/, '\u2212') : f.placeholder;
    ed.setElevDraft({ id: f.id, shown });
    const box = ed.layout().elevs.get(f.id);
    const r = ed.anchorRect(f.id, true);
    if (!box?.tag || !r) return;
    // the plan never moves for the pad: the pad steps off the tag, once the plan is still again
    if (pad.shown && ed.inMotion) {
      clearTimeout(refitTimer);
      refitTimer = window.setTimeout(() => positionContext(), 200);
    } else if (pad.shown) placeShotPad(true);
    const vs = ed.view.scale;
    const size = box.size * vs;
    const t = box.tag;
    const st = f.el.style;
    st.left = `${r.x}px`;
    st.top = `${r.y}px`;
    st.width = `${r.w}px`;
    st.height = `${r.h}px`;
    st.fontSize = `${size}px`;
    st.lineHeight = `${t.line * vs}px`;
    st.borderRadius = `${ed.metrics.pillRadius * vs}px`;
    // each piece where the plan draws it: the first line's baseline at the tag's, the depth line under it
    const top = `${(t.base - t.line / 2 - 0.36 * box.size) * vs}px`;
    for (const [node, x] of [[f.n, t.padX], [f.word, t.wordsX], [f.value, t.valueX]] as const) {
      node.style.left = `${x * vs}px`;
      node.style.top = top;
    }
    f.value.textContent = shown;
    f.value.classList.toggle('empty', !f.text);
    f.depth.hidden = !t.depth;
    if (t.depth && t.depthSize && t.depthBase) {
      f.depth.textContent = t.depth;
      f.depth.style.left = `${t.wordsX * vs}px`;
      f.depth.style.fontSize = `${t.depthSize * vs}px`;
      f.depth.style.lineHeight = `${t.depthSize * 1.2 * vs}px`;
      f.depth.style.top = `${(t.depthBase - 0.96 * t.depthSize) * vs}px`;
    }
  }
  /** Typing into the shot is over: the tag shows what was typed (a shot left without one is gone); a slip of the finger is said. */
  function finishElevTyping(): void {
    const ed = editor;
    if (!ed?.elevEditing) return;
    ed.endElevEdit();
    // (done, the shot is let go, as a test placed is: the next tap places the next shot)
    ed.select(null);
  }

  function pickNoteColor(n: Note, anchor: HTMLElement): void {
    openPopover(anchor, (close) => [
      h('div', { class: 'chips wrap' }, ...Object.entries(NOTE_COLORS).map(([key, color]) => h('button', { class: 'chip', type: 'button', 'aria-pressed': String(n.color === key), onClick: () => (editor?.updateItem<Note>(n.id, (x) => ({ ...x, color: key })), haptic(), close()) }, h('span', { class: 'swatch', style: { background: color } }), key[0]!.toUpperCase() + key.slice(1)))),
    ], { label: 'Note colour' });
  }

  function unionRect(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): { x: number; y: number; w: number; h: number } {
    const x = Math.min(a.x, b.x),
      y = Math.min(a.y, b.y);
    return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
  }
  function centerOf(it: Item | undefined): Vec | null {
    if (!it || (it.t !== 'hl' && it.t !== 'area')) return null;
    const s = it.shape;
    if (s.k === 'stroke') {
      const i = Math.floor(s.pts.length / 4) * 2;
      return { x: s.pts[i] ?? 0, y: s.pts[i + 1] ?? 0 };
    }
    if (s.k === 'rect') return { x: s.x + s.w / 2, y: s.y + s.h / 2 };
    let x = 0,
      y = 0;
    for (let i = 0; i < s.pts.length; i += 2) {
      x += s.pts[i]!;
      y += s.pts[i + 1]!;
    }
    return { x: x / (s.pts.length / 2), y: y / (s.pts.length / 2) };
  }

  /* ------------------------------------------------------- share / more */

  let exportCache: { doc: MapDoc; format: string; blob: Promise<Blob> } | null = null;
  const exportBlob = (format: 'png' | 'jpeg'): Promise<Blob> => {
    if (!editor) return Promise.reject(new Error('no editor'));
    // a shot being typed is finished first: the map goes out as it stands
    editor.endElevEdit();
    if (exportCache && exportCache.doc === editor.doc && exportCache.format === format) return exportCache.blob;
    const blob = editor.export(format);
    exportCache = { doc: editor.doc, format, blob };
    return blob;
  };
  shareBtn.addEventListener('click', () => {
    if (!editor) return;
    const format = app.state.settings.exportFormat;
    const name = safeFilename(mapTitle, format === 'jpeg' ? 'jpg' : 'png');
    const p = exportBlob(format);
    shareBtn.disabled = true;
    void p
      .then(async (blob) => {
        const file = new File([blob], name, { type: blob.type });
        const r = await shareFile(file);
        if (r === 'unsupported' || r === 'failed') {
          downloadBlob(blob, name);
          toast(r === 'failed' ? 'Sharing failed — saved a download instead' : 'Downloaded', 'info');
        } else if (r === 'shared') toast('Shared', 'success');
      })
      .catch(() => toast('Export failed', 'error'))
      .finally(() => (shareBtn.disabled = false));
  });
  moreBtn.addEventListener('click', () => {
    if (!editor) return;
    const ed = editor;
    // in sections, as a system menu is: the picture out, what the map shows, the map itself
    openMenu(moreBtn, [
      { label: 'Copy image', icon: ICONS.copy, onSelect: () => void copyPng(exportBlob('png')).then((ok) => toast(ok ? 'Copied — paste it into your report' : 'Copy is not available here', ok ? 'success' : 'error')) },
      // the shots as lines of text, for the report's narrative or table
      ...(ed.doc.items.some((i) => i.t === 'elev') ? [{ label: 'Copy elevations as text', icon: ICONS.elev, onSelect: () => void copyText(elevationsText(ed.doc.items, ed.doc.elev)).then((ok) => toast(ok ? 'Elevations copied — paste them into your report' : 'Copy is not available here', ok ? 'success' : 'error')) }] : []),
      { label: 'Save to Files', icon: ICONS.folder, onSelect: () => void exportBlob(app.state.settings.exportFormat).then((b) => downloadBlob(b, safeFilename(mapTitle, app.state.settings.exportFormat === 'jpeg' ? 'jpg' : 'png'))) },
      MENU_SEP,
      { label: ed.doc.legend ? 'Hide legend' : 'Show legend', icon: ICONS.list, onSelect: () => ed.patchDoc({ legend: !ed.doc.legend }) },
      // the pinpoints where the leaders end: a test's dot, a shot's ring — or none, the leaders ending on their spots
      ...(ed.doc.kind === 'soils' || ed.doc.items.some((i) => i.t === 'elev')
        ? [{ label: 'Pinpoint dots', icon: ICONS.test, checked: !ed.doc.noDots, onSelect: () => {
            const { noDots: _was, ...rest } = ed.doc;
            ed.commit(ed.doc.noDots ? rest : { ...rest, noDots: true });
            haptic();
          } }]
        : []),
      { label: `Markup size: ${ed.doc.scale <= 0.9 ? 'Small' : ed.doc.scale >= 1.15 ? 'Large' : 'Normal'}`, icon: ICONS.ruler, onSelect: () => ed.patchDoc({ scale: ed.doc.scale <= 0.9 ? 1 : ed.doc.scale >= 1.15 ? 0.85 : 1.2 }) },
      // what MapMark reads the plan as: its lines alone, then over the plan by kind, then the plan again
      ...(ed.drawing
        ? [
            {
              label: ed.showLines === 'off' ? "Show the plan's lines" : ed.showLines === 'lines' ? 'Lines over the plan' : "Hide the plan's lines",
              icon: ICONS.eye,
              onSelect: () => (ed.showLines = ed.showLines === 'off' ? 'lines' : ed.showLines === 'lines' ? 'overlay' : 'off'),
            },
          ]
        : []),
      MENU_SEP,
      { label: 'Rename', icon: ICONS.edit, onSelect: () => void rename() },
      { label: 'Delete map', icon: ICONS.trash, danger: true, onSelect: () => void remove() },
    ]);
  });
  async function rename(): Promise<void> {
    const v = await promptSheet('Rename map', { value: mapTitle, label: 'Title (also the export file name)' });
    if (v === null || !v.trim()) return;
    mapTitle = v.trim();
    titleText.textContent = mapTitle;
    title.classList.toggle('empty', !mapTitle);
    await renameMap(id, mapTitle);
  }
  async function remove(): Promise<void> {
    if (!(await confirmSheet('Delete this map?', 'It is removed from every device once they sync.'))) return;
    clearTimeout(saveTimer);
    await deleteMap(id);
    navigate({ name: 'library' }, true);
  }
  title.addEventListener('click', () => void rename());
  undoBtn.addEventListener('click', () => editor?.undo());
  redoBtn.addEventListener('click', () => editor?.redo());

  /* --------------------------------------------------------- keyboard */

  const onKey = (e: KeyboardEvent) => {
    if (!editor) return;
    // a menu or popover open has the keys (Esc puts it away): nothing acts behind it
    if (menuOpen()) return;
    // a shot being typed takes the keyboard attached to the iPad: digits, point, minus, Delete; Return, Esc or Tab finish it
    if (editor.elevEditing && !document.querySelector('dialog[open]') && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const k = e.key;
      // (a thousands comma is no point: it is let go, as any other key)
      const pk: PadKey | null = /^[0-9]$/.test(k) ? (k as PadKey) : k === '.' ? '.' : k === '-' || k === '−' ? 'sign' : k === 'Backspace' || k === 'Delete' ? 'back' : null;
      if (pk) {
        e.preventDefault();
        return typeOnShot(pk);
      }
      if (k === 'Enter' || k === 'Escape' || k === 'Tab') {
        e.preventDefault();
        return finishElevTyping();
      }
    }
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (target && context.contains(target) && e.key !== 'Escape') return;
    if (document.querySelector('dialog[open]')) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) editor.redo();
      else editor.undo();
      return;
    }
    if (mod && (e.key === '=' || e.key === '+')) return (e.preventDefault(), editor.zoomBy(1.25));
    if (mod && e.key === '-') return (e.preventDefault(), editor.zoomBy(0.8));
    if (mod && e.key === '0') return (e.preventDefault(), editor.fit());
    if (e.key === 'Enter' && editor.building !== null) return (e.preventDefault(), editor.finishBuilding());
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (editor.building !== null && editor.buildingSelection !== null) editor.deleteBuildingPoint();
      else if (editor.building !== null) editor.undoBuildingPoint();
      else if (editor.vertexSelection !== null) editor.deleteVertex();
      else editor.deleteSelection();
      return;
    }
    if (e.key === 'Escape') {
      // (the side panel first: it is what was opened last)
      if (side.key()) return side.close();
      if (editor.building !== null) return editor.cancelBuilding();
      if (editor.cutting) return editor.setCutting(false);
      if (editor.pointEditing) return editor.editPoints(false);
      if (editor.pointing) return editor.setPointing(false);
      // placing tests (or shots): a selected one is let go first, then the tool
      if ((editor.tool === 'test' || editor.tool === 'elev') && editor.selection) return editor.select(null);
      if (editor.tool !== 'mark') return editor.putAwayTool();
      return editor.select(null);
    }
    const idx = Number(e.key) - 1;
    if (!mod && idx >= 0 && idx < TOOLS[editor.doc.kind].length) {
      const list = TOOLS[editor.doc.kind];
      const t = list[idx];
      // (Shapes: a square; pressed again, the other shape)
      if (t) editor.setTool(t.id === 'shape' ? (editor.tool === 'square' ? 'circle' : 'square') : t.id);
    }
    if (!mod && (e.key === '+' || e.key === '=')) editor.zoomBy(1.25);
    if (!mod && e.key === '-') editor.zoomBy(0.8);
    if (!mod && e.key === '0') editor.fit();
  };
  document.addEventListener('keydown', onKey, { signal: ac.signal });
  window.addEventListener('pagehide', () => void flushSave(), { signal: ac.signal });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushSave();
  }, { signal: ac.signal });

  /* ------------------------------------------------------------- load */

  void (async () => {
    try {
      const map = await getMap(id);
      if (destroyed) return;
      if (!map || map.deletedAt) {
        toast('That map is not on this device yet.', 'error');
        navigate({ name: 'library' }, true);
        return;
      }
      mapTitle = map.title;
      titleText.textContent = mapTitle;
      title.classList.toggle('empty', !mapTitle);
      // (the plan may have been read and decoded already, while the finger was still on the card: warm.ts)
      const warmed = takeWarm(id);
      const [blob] = await Promise.all([(async () => (await warmed?.blob) ?? (await mapImage(id)) ?? ensureImage(id))(), loadFonts()]);
      if (!blob) throw new Error('Image not available');
      // the plan lifted under the marks (so they read on a shaded plan), made off the main thread
      const lift = liftInWorker(`${id}:${blob.size}`, blob);
      // Analyse the plan's line work off the main thread (decoded there, as it is here): strokes snap
      // to it once ready; its stationing and its drawing come a moment after, for the editor once made
      let whenEditor!: (e: Editor) => void;
      const editorMade = new Promise<Editor>((r) => (whenEditor = r));
      const job = analysePlan(blob, map.doc.kind === 'concrete');
      void Promise.all([job.stations, editorMade]).then(([st, ed]) => {
        if (destroyed) return;
        if (st) ed.setStationing(st);
        stage.dataset.stations = st ? String(st.alignments.length) : '0';
      });
      void Promise.all([job.drawing, editorMade]).then(([dr, ed]) => {
        if (destroyed) return;
        ed.setDrawing(dr);
        stage.dataset.drawing = dr ? String(dr.items.length) : '0';
      });
      void Promise.all([job.engine, editorMade])
        .then(([engine, ed]) => {
          if (!destroyed) ed.setEngine(engine);
        })
        .catch((e) => console.warn('plan analysis failed', e));
      imageBitmap = (await warmed?.bitmap) ?? (await createImageBitmap(blob, { imageOrientation: 'from-image' }));
      // (left while the plan was decoded: it is let go here, the screen gone)
      if (destroyed) {
        imageBitmap.close();
        imageBitmap = null;
        return;
      }
      // a map with marks is first drawn with them lifted, if that is a moment away (the zoom flies its card meanwhile)
      let lifted: ImageBitmap | null | undefined;
      if (map.doc.items.some((i) => i.t === 'area' || i.t === 'hl')) lifted = await Promise.race([lift, new Promise<undefined>((r) => setTimeout(r, 450))]);
      if (destroyed) return;
      // The callouts already on the map (a new one, filled in from the plan, may still want a word typed)
      for (const it of map.doc.items) if (it.t === 'callout') seenCallouts.add(it.id);
      // Continue numbering from other maps on this device (same date).
      const d = map.doc;
      for (const other of app.state.maps) if (other.id !== id) for (const it of other.doc.items) if (it.t === 'test' && it.date === d.soils.date && it.n > numberFloor[methodOf(it)]) numberFloor[methodOf(it)] = it.n;
      const ed = new Editor(stage, map.doc, imageBitmap, { fingerPansAfterPen: app.state.settings.fingerPansAfterPen, snapAnchors: app.state.settings.snapAnchors }, {
        change: (doc) => {
          // something was made or changed: the hint has had its moment
          for (const hint of HINTS) dismissToast(hint);
          scheduleSave(doc);
          updateHistoryButtons();
          renderInfobar();
          renderSubtools();
          renderContext();
        },
        selection: (id) => {
          renderSubtools();
          renderInfobar();
          renderContext();
          // a fresh callout: start typing straight away — unless the plan said it all (its stations, and a
          // material word and alignment to go with them), when it is done
          const it = id ? editor!.getItem(id) : undefined;
          const fresh = !!it && it.t === 'callout' && !seenCallouts.has(it.id);
          if (it && it.t === 'callout' && (calloutIsEmpty(it) || (fresh && it.auto && (it.materialName === '' || !it.alignment.trim())))) beginCalloutEdit(it.id, null);
          if (it && it.t === 'callout') seenCallouts.add(it.id);
        },
        tool: (tool) => {
          renderToolbar();
          renderSubtools();
          // (a soils map's strip is about the tool in hand)
          renderInfobar();
          renderContext();
          // say what the next tap does; another tool's hint has had its moment
          const hint = hintFor(tool, map.doc.kind);
          // (long enough to be read, and kept until the tool is used: Adam, 2026-10-24, "disappearing way too quickly")
          if (hint) toast(hint, 'info', 7000);
          for (const other of HINTS) if (other !== hint) dismissToast(other);
        },
        notice: (text) => toast(text, 'info', 3000),
        warn: (text) => toast(text, 'error', 4500),
        viewChanged: () => positionContext(),
        dragging: (active) => {
          // the panel steps aside while its item moves and settles back beside it after
          if (active) {
            endCalloutEdit();
            context.classList.remove('show');
          } else renderContext();
        },
        // carried onto (or off) a place where it may not rest: felt in the hand
        resting: () => haptic(),
        hold: (state) => {
          // no ring under the finger: a hold is felt (haptic) and then seen as the band itself
          if (state === 'armed') haptic();
        },
        // a point took hold of the plan, or a rest became a corner: felt where the device can (it is always seen)
        snapped: () => haptic(),
        // corners placed one at a time: the bar at the top says what the next tap does
        building: () => (renderContext(), updateHistoryButtons()),
        intent: () => {},
      });
      editor = ed;
      whenEditor(ed);
      if (lifted !== undefined) ed.setLift(lifted);
      else void lift.then((b) => (destroyed ? undefined : ed.setLift(b)));
      // the glass over the plan is drawn from it, and follows it: afresh every frame it is drawn
      // (once the plan is what is on screen: not while the zoom into it is in the air)
      setGlassSource({ layers: () => ed.layers, backdrop: () => getComputedStyle(stage).getPropertyValue('--bg-editor').trim() || '#e3e3e8', ready: () => !destroyed && !screen.classList.contains('arriving') && !stage.classList.contains('loading') });
      ed.onFrame = refreshGlass;
      // an undo or redo while a callout is typed into ends the typing: its fields would say what the map no longer does
      ed.onHistory = () => {
        if (editingId) endCalloutEdit();
      };
      // the floating controls, as the plan sees them: a selected shape's handles are kept out from under them
      ed.coveredBy = () => {
        const at = stage.getBoundingClientRect();
        return [backBtn, title, ...topChrome.querySelectorAll('.cluster.trail > *'), toolbar, subtools.el, infobar.el, zoomer]
          .filter((el) => !(el as HTMLElement).hidden && (el as HTMLElement).offsetParent !== null)
          .map((el) => el.getBoundingClientRect())
          .map((r) => ({ x: r.left - at.left, y: r.top - at.top, w: r.width, h: r.height }));
      };
      lastSaved = thumbOf = ed.doc;
      // Honour the cross-map number floor when placing tests.
      const origAdd = ed.addTest.bind(ed);
      ed.addTest = (p: Vec) => origAdd(p, numberFloor[ed.testMethod]);
      stage.classList.remove('loading');
      renderZoomer();
      // the tools rise into place once there is a plan to use them on
      dock.hidden = false;
      zoomer.hidden = false;
      dock.classList.add('ready');
      renderToolbar();
      renderSubtools();
      renderInfobar();
      updateHistoryButtons();
      barRoomAt = null;
      // the plan is drawn now, not on the next frame: the zoom into this map is waiting for its picture
      ed.flush();
      zoom.editorReady(stage);
      // a map with nothing on it yet says what the tool in hand does, once the tools are in place
      if (!ed.doc.items.length)
        setTimeout(() => {
          const hint = hintFor(ed.tool, ed.doc.kind);
          if (!destroyed && hint && !ed.doc.items.length && ed.building === null) toast(hint, 'info', 7000);
        }, 550);
    } catch (e) {
      console.error(e);
      // (a map left while it loaded says nothing: another may be open by now)
      if (destroyed) return;
      toast('Could not open this map.', 'error');
      navigate({ name: 'library' }, true);
    }
  })();

  return () => {
    destroyed = true;
    ac.abort();
    sideAc.abort();
    for (const hint of HINTS) dismissToast(hint);
    // whatever this screen opened goes with it: no sheet or menu stays over the next one, acting on a map no longer open
    closeAll();
    setGlassSource(null);
    for (const u of untrackGlass) u();
    padKeepOut(null);
    void flushSave();
    editor?.destroy();
    editor = null;
    imageBitmap?.close();
    imageBitmap = null;
    screen.remove();
    // (a pad left for one of its fields goes with it)
    padGone();
  };
}
